/* ============================================================
   ATOME : Build My Box (pooled-box pricing, live-parity item count, round 15)
   Vanilla JS state machine. No dependencies.

   ROUND 15 CORRECTION (Aug 18 2026, Lucas): round 13's per-item credit
   WEIGHTS (croissant pack = 2, butter = 4, etc.) are gone. The live
   Build Your Own Box already IS the credit system: 1 item qty = 1 box
   slot. Customers pick a box TIER first (6/10/12, 1:1 with the live
   BYB6/10/12 variants of build-your-box-1), then fill it with items,
   each unit of qty consuming exactly one slot, same as the live
   builder's bundleCount() vs STATE.selectedSize. No merchant-editable
   weight map, no per-item badge, no overflow-blocked-with-upgrade-link
   affordance: adding past the current tier auto-upsizes to the next
   tier that fits (autoSelectSize(), copied from bab-custom.js), exactly
   like live, only ever stopping at the largest configured tier.
   Checkout is still the fixed-bundle model copied from
   assets/bab-custom.js's handleCheckout/getBundleId (one parent BYB
   line, Item N properties, selling_plan on the parent only).

   Still renamed bab* -> bmb* / BAB_DATA -> BMB_DATA throughout so
   nothing here collides with the still-live builder: separate DOM ids,
   CSS classes, localStorage key (atome_bmb_cart_v4, bumped this
   round so extrasItems can restore without a v3 cart missing that
   field), sessionStorage key
   (bmb_state), i18n dictionary ids (#bmb-i18n / #bmb-i18n-items / #bmb-i18n-extras).
   ============================================================ */

(function () {
  'use strict';

  /* --------------------------------------------------------
     1. DATA & STATE
  -------------------------------------------------------- */
  const DATA = window.BMB_DATA || {};

  const ITEMS          = DATA.items || [];               // catalogue: [{variantId,productId,collectionId,collectionHandle,title,price,comparePrice,image,available,description,servings,unitLabel,isVegan,isNew,comingSoon,needsPan}]
  const EXTRAS         = DATA.extras || [];              // add-ons: [{variantId,productId,title,price,comparePrice,image,available,description}] not box slots

  // ── Atome Pan (copied from bab-custom.js's PAN/CAST_IRON/NEEDS_PAN_VARIANT_IDS) ──
  const PAN = DATA.pan || null;
  const CAST_IRON = DATA.castIron || null;
  const CAST_IRON_NO_DISCOUNT = DATA.castIronNoDiscount || false;
  // Extras collection does not include The Atome Pan. Extra pans are
  // catalog $40 add-ons, so expose the pan in the extras drawer at
  // compare/catalog dollars, never the box $10 promo.
  if (PAN && PAN.variantId && !EXTRAS.some(function (e) { return e.variantId === PAN.variantId; })) {
    EXTRAS.push({
      variantId: PAN.variantId,
      productId: PAN.productId,
      title: PAN.title,
      price: PAN.comparePrice || 4000,
      comparePrice: null,
      image: PAN.image,
      available: true,
      description: ''
    });
  }
  var NEEDS_PAN_VARIANT_IDS = new Set();
  ITEMS.forEach(function (i) { if (i.needsPan) NEEDS_PAN_VARIANT_IDS.add(i.variantId); });

  // Best Sellers golden row (round 6, Aug 16 2026): block-configured picks
  // resolved server-side to real variant IDs, mirroring bab-custom.js's
  // BEST_SELLERS/fillBestSellers() pattern (see JOURNAL for the picks).
  const BEST_SELLERS = DATA.bestSellers || [];
  BEST_SELLERS.forEach(function (bs) { if (bs.needsPan) NEEDS_PAN_VARIANT_IDS.add(bs.variantId); });

  // Recharge extract allowlist: only these five live BYB collections.
  // Old BMB collection IDs (329093* / 329094*) fail first-sub explode.
  var LIVE_BYB_COLLECTION_IDS = {
    '290902769847': true, // bread
    '290902802615': true, // pastry
    '309390934199': true, // fresh-pasta
    '310110257335': true, // vegan-pastries
    '327228227767': true  // gourmet
  };
  var BMB_TO_LIVE_COLLECTION_ID = {
    '329093775543': '290902769847', // bmb-breads -> bread
    '329093808311': '290902802615', // bmb-pastries -> pastry
    '329093841079': '309390934199', // bmb-fresh-pasta -> fresh-pasta
    '329093873847': '310110257335', // bmb-vegan-pastries -> vegan-pastries
    '329094135991': '327228227767'  // bmb-gourmet-food -> gourmet
  };

  function catalogItemForVariant(variantId) {
    var vid = parseInt(variantId, 10);
    if (!vid) return null;
    for (var i = 0; i < ITEMS.length; i++) {
      if (ITEMS[i].variantId === vid) return ITEMS[i];
    }
    return null;
  }

  function toLiveCollectionId(raw) {
    var id = String(raw == null ? '' : raw);
    if (!id) return '';
    if (BMB_TO_LIVE_COLLECTION_ID[id]) id = BMB_TO_LIVE_COLLECTION_ID[id];
    return LIVE_BYB_COLLECTION_IDS[id] ? id : '';
  }

  // Prefer live ITEMS catalogue id; remap stale BMB ids to the twin;
  // empty string means not allowlisted (checkout hard-fails).
  function resolveLiveCollectionId(variantId, fallbackId) {
    var catalog = catalogItemForVariant(variantId);
    var live = catalog ? toLiveCollectionId(catalog.collectionId) : '';
    if (live) return live;
    return toLiveCollectionId(fallbackId);
  }

  function remapPersistedItemCollectionIds() {
    STATE.items.forEach(function (item) {
      if (!item || item.isGift) return;
      var live = resolveLiveCollectionId(item.variantId, item.collectionId);
      if (live) item.collectionId = live;
    });
  }

  /* ============================================================
     Pooled-box tiers (Aug 18 2026 pivot): read live from build-your-box-1
     (BYB6/10/12), same boxSizes shape bab-custom.js's BOX_SIZES uses
     ([{size,label,variantId,price,comparePrice,sellingPlans:[{id,name,
     price,discountPercent}]}]). No fallback IDs: this product is live
     and published, so BMB_DATA.boxSizes always resolves.
     ============================================================ */
  const BOX_SIZES = DATA.boxSizes || [];
  const BYB_PRODUCT_ID = DATA.productId || '';

  const SELLING_PLANS   = DATA.sellingPlans || [];         // [{id,name,discountPercent}] read live from build-your-box-1 itself
  const FREE_SHIP_CENTS = DATA.freeShippingCents || 14900; // tier's ONE-TIME price >= this -> free shipping messaging
  const CART_FREE_SHIP_CENTS = DATA.cartFreeShippingThresholdCents || 14900;
  const RC_TOKEN        = DATA.rcToken || '';

  /* ============================================================
     Shipping estimate popup (round 14, Aug 18 2026): display-only
     coverage cap + optional app-proxy path override. Never reads or
     writes live f2c-delivery's own freeShippingMaxCoverage config.
     ============================================================ */
  const SHIPCALC_MAX_COVERAGE_CENTS = DATA.shipcalcMaxCoverageCents || 4000;
  const SHIPCALC_PROXY_OVERRIDE = DATA.shipcalcProxyPath || '';
  const DELIVERY_AREA_URL = DATA.deliveryAreaUrl || '/pages/delivery-area';

  /* ============================================================
     Optional event space (cart redesign, Aug 26 2026): schema-driven.
     event_label empty -> the whole block never renders (zero reserved
     space). event_deadline (ISO datetime string) set -> a live
     "Order within X days Y hrs" countdown renders under the label and
     the block hides itself once the deadline passes. Display-only.
     ============================================================ */
  const EVENT_LABEL = (DATA.eventLabel || '').trim();
  const EVENT_DEADLINE = (function () {
    if (!DATA.eventDeadline) return null;
    var d = new Date(DATA.eventDeadline);
    return isNaN(d.getTime()) ? null : d;
  })();

  // UI strings : read from the hidden #bmb-i18n DOM so Weglot can translate
  // them (same pattern as the live builder's #bab-i18n). English defaults
  // here; refreshI18n() layers in the built-in French fallback below, then
  // lets any genuine Weglot translation win.
  var S = {
    chooseItems:     'Add breads, pastries & extras to build your box',
    itemsSelected:    'items selected',
    shipNode:         'Free shipping',
    shippingFeeApplies: 'Shipping fee applies',
    footerUpgrade10:  'Upgrade to 10 for free shipping',
    addMoreItems:     'Add {n} more item to checkout',
    addMoreItemsPlural: 'Add {n} more items to checkout',
    addStickyItem:    'Add {n} item',
    addStickyItems:   'Add {n} items',
    stickyContinue:   'Continue',
    upgradeToAndSave: 'Upgrade to {n} and save {pct}%',
    alertBoxNotFound: 'Could not find that box size. Please refresh and try again.',
    reviewYourBox:    'Review Your Box',
    reviewMyBox:      'Review my box',
    yourSelections:   'Your selections',
    clearBox:         'Clear box',
    clearBoxConfirm:  'Sure? Clear',
    noItemsYet:       'No items yet',
    yourBox:          'Your Box',
    yourBoxGift:      'Your Gift Box',
    pageTitleGift:    'Build Your Gift Box',
    chooseItemsGift:  'Add breads, pastries & extras to build your gift box',
    remove:           'Remove',
    free:             'FREE',
    trcGiftTitle:     'Chocolate Croissant Bites (8-pack)',
    trcGiftLabel:     'Truth and Reconciliation Day gift',
    included:         'Included',
    bogoNote:         'Buy 1, get 1 free',
    bogoDealTitle:    'Limited add-on deal',
    bogoDealCopy:     'One-time deal, ships with this box only. Buy 1, get 1 free, up to 4 free.',
    bogoTimerLabel:   'Ends in',
    deliveryPlan:     'Delivery plan',
    oneTime:          'One-time',
    oneTimePurchase:  'One-time purchase',
    addGiftNote:      'Add gift note',
    giftNote:         'Gift note',
    giftNotePlaceholder: 'Write a short note for the recipient',
    giftNoteSave:     'Save',
    giftNoteCancel:   'Cancel',
    giftNoteEdit:     'Edit',
    giftNoteRemove:   'Remove',
    subscription:     'Subscription',
    subscribeSave:    'Subscribe & Save',
    perBox:           '/ box',
    deliveryEvery:    'Delivery every {n} days',
    deliveryEveryShort: 'Every {n} days',
    itemsCount:       'items',
    cancelAnytime:    'Cancel anytime',
    subtotal:         'Subtotal',
    total:            'Total',
    checkout:         'Checkout',
    ingredients:      'Ingredients',
    allergens:        'Allergens',
    bakingInstructions: 'Baking Instructions',
    nutritionFacts:   'Nutrition Facts',
    noAdditionalInfo: 'No additional information available.',
    alertEmptyBox:    'Please add at least one item to your box before checking out.',
    alertCartFail:    'Could not prepare your cart. Please try again.',
    alertCartError:   'Could not add items to cart.',
    coTitle:        'We couldn’t set up your box',
    coBodyFirst:    'Nothing was charged. This usually works on a second try.',
    coBodyCollection: 'Nothing was charged. One item in your box is missing a valid category. Remove it, add it again, then retry.',
    coBodyAgain:    'Still not working. Email us at hello@atomebakery.com and we’ll finish your order with you.',
    coRetry:        'Try again',
    coBack:         'Back to my box',
    coEmailSubject: 'Build Your Box checkout help',
    coEmailBody:    'Hi Atome, checkout could not set up my box. Reference: {ref}. My box: {box}.',
    addBtn:           '+ Add',
    outOfStock:       'Out of Stock',
    comingSoon:       'Coming back soon',
    searchPlaceholder:'🔍 Search…',
    vegan:            '🌱 Vegan',
    new:              'New',
    mostPopular:      'Most popular',
    save:             'SAVE',
    skipCancelAnytime:'Skip or cancel anytime',
    subPerkDiscount:  '{pct}% off every box',
    noCommitment:     'No commitment',
    subInfoTitle:     'Subscribe & save',
    subInfoAria:      'Subscription details',
    renewsAuto:       'Renews automatically',
    noFee:            'no fee',
    perCredit:        '/credit',
    estimateShipping: 'Check shipping to your postal code',
    shipcalcTitle:    'Estimate shipping',
    shipcalcSubtitle: 'Enter your postal code to estimate your delivery cost and date.',
    shipcalcCheckBtn: 'Check',
    shipcalcLoading:  'Checking delivery to your area…',
    shipcalcErrEmpty: 'Please enter your postal code.',
    shipcalcErrInvalid: 'Please enter a valid Canadian postal code (e.g. V6B 1A1).',
    shipcalcErrGeneric: "We're temporarily unable to check delivery availability. Please try again or contact support.",
    shipcalcErrRateLimited: 'Too many requests. Please wait a moment and try again.',
    shipcalcIneligible: "We're unable to deliver to this area.",
    shipcalcEstimateDisclaimer: 'Estimate only, final rate and date are confirmed at checkout.',
    shipcalcDryIceNote: 'Includes insulated packaging and dry ice to keep your order fresh in transit.',
    shipcalcDeliveryEst: 'Estimated delivery: {date}',
    shipcalcFreeChip: 'Free shipping on this box',
    shipcalcUpgradeBtn: 'Upgrade to 10 credits, get free shipping',
    shipcalcRangeTo:  'to',
    shipcalcStartingAround: 'Starting around {price}',
    shipcalcClose:     'Close',
    selectSizeStep:   'Select size',
    selectFreqStep:   'Select frequency',
    fillBoxStep:      'Fill your box',
    yourBakingPan:    'Your baking pan',
    panNeededForBreads: 'Needed to bake the breads in your box',
    whyNeedPan:       'Why do you need a pan?',
    whyNeedPanTitle:  '🥐 Why you need a pan',
    panSteamTrap:     'The Steam Trap',
    panSteamTrapDesc: 'Trapped moisture lets dough develop slowly, creating a perfectly crispy crust',
    panMadeForAtome:  'Made for Atome',
    panMadeForAtomeDesc: 'All our products are designed to fit our pan perfectly',
    panWithout:       'Without a pan ⚠️',
    panWithoutDesc:   'The crust dries first, preventing the dough from developing properly',
    panLightOrPremium: 'Light or Premium',
    panLightOrPremiumDesc: 'Choose the Atome Pan ($10) or upgrade to the Cast-Iron Pan ($120)',
    panDisclaimer:    "Atome products require a closed-lid pan to bake. You may use a pullman pan or a dutch oven if you don't want to purchase our pan.",
    panPromptHeadline: 'Your loaf needs the pan to bake right',
    panPromptSupport: 'Shape and crust done right, a $40 pan for $10.',
    panPromptYes:     'Yes, add the pan',
    panPromptNo:      'I have one',
    panPromptAdded:   "Pan added. Your loaf's covered.",
    panPromptReadd:   'Changed your mind? Add the Atome Pan anytime.',
    panGateHint:      'Quick question: choose a pan option above',
    choosePanOption:  'Choose pan option',
    addTheAtomePan:   'Add The Atome Pan',
    upgradeToCastIron:'Upgrade to Cast Iron',
    switchToAluminium:'Switch to Aluminium Pan',
    extras:           'Extras',
    addExtras:        'Add extras',
    continueToExtras: 'Continue to extras',
    addExtrasTitle:   'Add extras',
    searchExtras:     'Search extras',
    searchExtrasLabel:'Search extras',
    noExtrasAvailable:'No extras available right now',
    noExtrasMatch:    'No extras match your search',
    quantity:         'Quantity',
    removeOne:        'Remove one',
    addOne:           'Add one',
    itemsChosen:      '{n} of {t} items chosen',
    emptySlot:        'Empty slot',
    moreSlots:        '+{n} more slots',
    orderWithin:      'Order within {time}',
    unitDay:          'day',
    unitDays:         'days',
    unitHr:           'hr',
    unitHrs:          'hrs',
    unitMin:          'min',
    buyOnce:          'Buy it once',
    switchToSub:      'Switch to Subscribe & Save',
    noSubscription:   'No subscription, one charge',
    perUnit:          '{price} per {unit}',
    inABox:           '{price} in a box',
    bundleUnit:       'bundles',
    popular:          'Popular',
    bestValue:        'Best value',
  };
  var S_EN = {};
  for (var _k in S) { if (Object.prototype.hasOwnProperty.call(S, _k)) S_EN[_k] = S[_k]; }

  // Built-in French (Canada) fallback for the fixed UI chrome, same reasoning
  // as the live builder: Weglot does not reliably translate JS-rendered
  // strings that have no visible English twin anywhere on the page.
  var S_FR = {
    chooseItems:     'Ajoutez pains, patisseries et extras pour composer votre boite',
    itemsSelected:    'articles selectionnes',
    shipNode:         'Livraison gratuite',
    shippingFeeApplies: 'Des frais de livraison s\'appliquent',
    footerUpgrade10:  'Passez a 10, livraison gratuite',
    addMoreItems:     'Ajoutez {n} article de plus pour continuer',
    addMoreItemsPlural: 'Ajoutez {n} articles de plus pour continuer',
    addStickyItem:    'Ajoutez {n} article',
    addStickyItems:   'Ajoutez {n} articles',
    stickyContinue:   'Continuer',
    upgradeToAndSave: 'Passez a {n} et economisez {pct}%',
    alertBoxNotFound: "Impossible de trouver cette taille de boite. Veuillez rafraichir et reessayer.",
    reviewYourBox:    'Verifiez votre boite',
    reviewMyBox:      'Voir ma bo\u00EEte',
    yourSelections:   'Vos selections',
    clearBox:         'Vider la boite',
    clearBoxConfirm:  'Confirmer ?',
    noItemsYet:       'Aucun article pour le moment',
    yourBox:          'Votre boite',
    yourBoxGift:      'Votre boite cadeau',
    pageTitleGift:    'Composez votre boite cadeau',
    chooseItemsGift:  'Ajoutez pains, patisseries et extras pour composer votre boite cadeau',
    remove:           'Retirer',
    free:             'GRATUIT',
    trcGiftLabel:     'Cadeau de la Journee nationale de la verite et de la reconciliation',
    included:         'Inclus',
    bogoNote:         '1 acheté, 1 offert',
    bogoDealTitle:    'Offre extra limitée',
    bogoDealCopy:     'Offre unique, livrée uniquement avec cette box. 1 acheté, 1 offert, jusqu\'à 4 offerts.',
    bogoTimerLabel:   'Se termine dans',
    deliveryPlan:     'Plan de livraison',
    oneTime:          'Achat unique',
    oneTimePurchase:  'Achat unique',
    addGiftNote:      'Ajouter une note cadeau',
    giftNote:         'Note cadeau',
    giftNotePlaceholder: 'Ecrivez une courte note pour le destinataire',
    giftNoteSave:     'Enregistrer',
    giftNoteCancel:   'Annuler',
    giftNoteEdit:     'Modifier',
    giftNoteRemove:   'Retirer',
    subscription:     'Abonnement',
    subscribeSave:    'Abonnez-vous et economisez',
    perBox:           '/ boite',
    deliveryEvery:    'Livraison tous les {n} jours',
    deliveryEveryShort: 'Tous les {n} jours',
    itemsCount:       'articles',
    cancelAnytime:    'Annulez a tout moment',
    subtotal:         'Sous-total',
    total:            'Total',
    checkout:         'Passer a la caisse',
    ingredients:      'Ingredients',
    allergens:        'Allergenes',
    bakingInstructions: 'Instructions de cuisson',
    nutritionFacts:   'Valeur nutritive',
    noAdditionalInfo: 'Aucune information supplementaire disponible.',
    alertEmptyBox:    'Veuillez ajouter au moins un article a votre boite avant de passer a la caisse.',
    alertCartFail:    'Impossible de preparer votre panier. Veuillez reessayer.',
    alertCartError:   'Impossible ajouter les articles au panier.',
    coTitle:        'Impossible de preparer votre boite',
    coBodyFirst:    'Rien n’a ete facture. En general, un deuxieme essai suffit.',
    coBodyCollection: 'Rien n’a ete facture. Un article de votre boite n’a pas de categorie valide. Retirez-le, ajoutez-le a nouveau, puis reessayez.',
    coBodyAgain:    'Toujours bloque. Ecrivez-nous a hello@atomebakery.com et nous finaliserons votre commande avec vous.',
    coRetry:        'Reessayer',
    coBack:         'Retour a ma boite',
    coEmailSubject: 'Aide commande Build Your Box',
    coEmailBody:    'Bonjour Atome, le passage a la caisse n’a pas pu preparer ma boite. Reference : {ref}. Ma boite : {box}.',
    addBtn:           '+ Ajouter',
    outOfStock:       'Rupture de stock',
    comingSoon:       'Bientot de retour',
    searchPlaceholder:'🔍 Rechercher…',
    vegan:            '🌱 Vegan',
    new:              'Nouveau',
    mostPopular:      'Le plus populaire',
    save:             'ECONOMISEZ',
    skipCancelAnytime:'Suspendez ou annulez a tout moment',
    subPerkDiscount:  '{pct}% de rabais sur chaque boite',
    noCommitment:     'Aucun engagement',
    subInfoTitle:     'Abonnez-vous et economisez',
    subInfoAria:      "Details de l'abonnement",
    renewsAuto:       'Se renouvelle automatiquement',
    noFee:            'sans frais',
    perCredit:        '/credit',
    estimateShipping: 'Verifiez la livraison a votre code postal',
    shipcalcTitle:    'Estimer la livraison',
    shipcalcSubtitle: 'Entrez votre code postal pour estimer le cout et la date de livraison.',
    shipcalcCheckBtn: 'Verifier',
    shipcalcLoading:  'Verification de la livraison a votre secteur…',
    shipcalcErrEmpty: 'Veuillez entrer votre code postal.',
    shipcalcErrInvalid: 'Veuillez entrer un code postal canadien valide (ex. V6B 1A1).',
    shipcalcErrGeneric: "Impossible de verifier la disponibilite de livraison pour le moment. Veuillez reessayer ou nous contacter.",
    shipcalcErrRateLimited: 'Trop de demandes. Veuillez patienter un moment et reessayer.',
    shipcalcIneligible: "Nous ne pouvons pas livrer a ce secteur.",
    shipcalcEstimateDisclaimer: 'Estimation seulement, le tarif et la date finale sont confirmes au paiement.',
    shipcalcDryIceNote: "Comprend un emballage isole et de la glace seche pour garder votre commande fraiche en transit.",
    shipcalcDeliveryEst: 'Livraison estimee : {date}',
    shipcalcFreeChip: 'Livraison gratuite pour cette boite',
    shipcalcUpgradeBtn: 'Passez a 10 produits, livraison gratuite',
    shipcalcRangeTo:  'a',
    shipcalcStartingAround: 'A partir d\'environ {price}',
    shipcalcClose:     'Fermer',
    selectSizeStep:   'Choisissez la taille',
    selectFreqStep:   'Choisissez la frequence',
    fillBoxStep:      'Remplissez votre boite',
    yourBakingPan:    'Votre moule de cuisson',
    panNeededForBreads: 'Necessaire pour cuire les pains de votre boite',
    whyNeedPan:       "Pourquoi avez-vous besoin d'un moule ?",
    whyNeedPanTitle:  '🥐 Pourquoi un moule',
    panSteamTrap:     'Le piege a vapeur',
    panSteamTrapDesc: "L'humidite emprisonnee laisse la pate se developper lentement, pour une croute parfaitement croustillante",
    panMadeForAtome:  'Concu pour Atome',
    panMadeForAtomeDesc: "Tous nos produits sont concus pour s'adapter parfaitement a notre moule",
    panWithout:       'Sans moule ⚠️',
    panWithoutDesc:   "La croute seche en premier, empechant la pate de bien se developper",
    panLightOrPremium: 'Leger ou Premium',
    panLightOrPremiumDesc: 'Choisissez le moule Atome (10 $) ou passez au moule en fonte (120 $)',
    panDisclaimer:    "Les produits Atome necessitent un moule a couvercle ferme pour la cuisson. Vous pouvez utiliser un moule pullman ou une cocotte si vous ne voulez pas acheter notre moule.",
    panPromptHeadline: 'Votre pain a besoin du moule pour bien cuire',
    panPromptSupport: 'Forme et croute comme il faut, un moule a 40 $ pour 10 $.',
    panPromptYes:     'Oui, ajouter le moule',
    panPromptNo:      "J'en ai un",
    panPromptAdded:   'Moule ajoute. Votre pain est couvert.',
    panPromptReadd:   'Vous changez d\'avis ? Ajoutez le moule Atome a tout moment.',
    panGateHint:      'Petite question: choisissez une option pour le moule ci-dessus',
    choosePanOption:  'Choisissez une option de moule',
    addTheAtomePan:   'Ajouter le moule Atome',
    upgradeToCastIron:'Passer a la fonte',
    switchToAluminium:'Revenir au moule en aluminium',
    extras:           'Extras',
    addExtras:        'Ajouter des extras',
    continueToExtras: 'Continuer vers les extras',
    addExtrasTitle:   'Ajouter des extras',
    searchExtras:     'Rechercher des extras',
    searchExtrasLabel:'Rechercher des extras',
    noExtrasAvailable:'Aucun extra disponible pour le moment',
    noExtrasMatch:    'Aucun extra ne correspond a votre recherche',
    quantity:         'Quantite',
    removeOne:        'Retirer un',
    addOne:           'Ajouter un',
    itemsChosen:      '{n} article sur {t} choisis',
    emptySlot:        'Emplacement vide',
    moreSlots:        '+{n} autres emplacements',
    orderWithin:      "Commandez d'ici {time}",
    unitDay:          'jour',
    unitDays:         'jours',
    unitHr:           'h',
    unitHrs:          'h',
    unitMin:          'min',
    buyOnce:          'Achat unique',
    switchToSub:      'Passer a l\'abonnement',
    bundleUnit:       'produits',
    perUnit:          '{price} par {unit}',
    inABox:           '{price} dans une boite',
    noSubscription:   'Sans abonnement, un seul paiement',
    popular:          'Populaire',
    bestValue:        'Meilleure valeur'
  };
  var UNIT_FR = {
    baguette: 'baguette',
    loaf: 'pain',
    person: 'personne',
    croissant: 'croissant',
    'mini pastry': 'mini-viennoiserie',
    viennoise: 'viennoise',
    pastry: 'viennoiserie',
    dough: 'pate',
    cake: 'gateau',
    waffle: 'gaufre',
    cookie: 'biscuit',
    ciabatta: 'ciabatta',
    buns: 'pain',
    'apple turnover': 'chausson',
    twist: 'torsade',
    madeleine: 'madeleine',
    macaron: 'macaron',
    item: 'article'
  };
  var S_LANGS = { fr: S_FR };

  function bmbLang() {
    try {
      if (window.Weglot && typeof Weglot.getCurrentLang === 'function') {
        var l = Weglot.getCurrentLang();
        if (l) return String(l).toLowerCase().split('-')[0];
      }
    } catch (e) {}
    var m = document.cookie.match(/(?:^|;\s*)(?:wglang|wg_chosen_lang)=([a-z-]+)/i);
    if (m) return m[1].toLowerCase().split('-')[0];
    var hl = (document.documentElement.getAttribute('lang') || '').toLowerCase();
    if (hl) return hl.split('-')[0];
    return 'en';
  }

  function refreshI18n() {
    var changed = false;
    var lang = bmbLang();
    var pack = S_LANGS[lang] || null;

    for (var key in S_EN) {
      if (!Object.prototype.hasOwnProperty.call(S_EN, key)) continue;
      var want = (pack && pack[key] != null) ? pack[key] : S_EN[key];
      if (S[key] !== want) { S[key] = want; changed = true; }
    }

    var dict = document.getElementById('bmb-i18n');
    if (dict) {
      dict.querySelectorAll('[data-i18n]').forEach(function (span) {
        var k = span.dataset.i18n;
        if (!Object.prototype.hasOwnProperty.call(S, k)) return;
        var val = span.textContent.trim();
        if (val && val !== S_EN[k] && S[k] !== val) { S[k] = val; changed = true; }
      });
    }

    var itemsDict = document.getElementById('bmb-i18n-items');
    if (itemsDict) {
      itemsDict.querySelectorAll('[data-vid]').forEach(function (span) {
        var vid = parseInt(span.dataset.vid, 10);
        var val = span.textContent.trim();
        if (!val) return;
        var catalogItem = ITEMS.find(function (i) { return i.variantId === vid; });
        if (catalogItem && catalogItem.title !== val) { catalogItem.title = val; changed = true; }
        var inCart = STATE.items.find(function (i) { return i.variantId === vid; });
        if (inCart && inCart.title !== val) { inCart.title = val; changed = true; }
      });
    }

    var extrasDict = document.getElementById('bmb-i18n-extras');
    if (extrasDict) {
      extrasDict.querySelectorAll('[data-vid]').forEach(function (span) {
        var vid = parseInt(span.dataset.vid, 10);
        var val = span.textContent.trim();
        if (!val) return;
        var catalogExtra = EXTRAS.find(function (e) { return e.variantId === vid; });
        if (catalogExtra && catalogExtra.title !== val) { catalogExtra.title = val; changed = true; }
        var inExtras = STATE.extrasItems.find(function (i) { return i.variantId === vid; });
        if (inExtras && inExtras.title !== val) { inExtras.title = val; changed = true; }
      });
    }
    return changed;
  }

  // ── Gift via URL param (?gift=CODE) ─────────────────────
  function normalizeGiftCode(code) {
    return String(code || '').trim().toUpperCase();
  }
  function buildGiftPromoList() {
    var list = Array.isArray(DATA.giftPromos) ? DATA.giftPromos.slice() : [];
    if (list.length === 0 && DATA.giftCode && DATA.giftVariantId) {
      list.push({
        code: DATA.giftCode,
        variantId: DATA.giftVariantId,
        title: DATA.giftProductTitle || 'Gift',
        image: DATA.giftProductImage || '',
        startsAt: null,
        endsAt: null
      });
    }
    return list.filter(function (p) { return p && p.code && p.variantId; });
  }
  // QA override (Sam): ?gift_preview=1 forces dated promos on, only on an
  // unpublished preview theme (Shopify.theme.role !== 'main'). Sticks for
  // the tab session. No-op on the live theme. Pattern matches
  // trc-promo.liquid's ?trc_preview=1 handling.
  var BMB_GIFT_PREVIEW = false;
  try {
    var _bmbRole = window.Shopify && Shopify.theme && Shopify.theme.role;
    if (_bmbRole && _bmbRole !== 'main') {
      if (new URLSearchParams(window.location.search).get('gift_preview') === '1') {
        sessionStorage.setItem('bmb_gift_preview', '1');
      }
      BMB_GIFT_PREVIEW = sessionStorage.getItem('bmb_gift_preview') === '1';
    }
  } catch (e) {}
  // A promo with no startsAt/endsAt is unbounded (legacy/permanent codes
  // like FREECROISSANTS). A dated promo is active only within
  // [startsAt, endsAt). Unparseable dates fail closed (inactive).
  function isGiftPromoActiveNow(promo) {
    if (BMB_GIFT_PREVIEW) return true;
    var now = Date.now();
    if (promo.startsAt) {
      var startMs = Date.parse(promo.startsAt);
      if (isNaN(startMs)) {
        console.warn('[BMB] gift promo "' + promo.code + '" has an unparseable startsAt, treating as inactive');
        return false;
      }
      if (now < startMs) return false;
    }
    if (promo.endsAt) {
      var endMs = Date.parse(promo.endsAt);
      if (isNaN(endMs)) {
        console.warn('[BMB] gift promo "' + promo.code + '" has an unparseable endsAt, treating as inactive');
        return false;
      }
      if (now >= endMs) return false;
    }
    return true;
  }
  function resolveUrlGiftPromo() {
    var param = '';
    try { param = new URLSearchParams(window.location.search).get('gift') || ''; } catch (e) {}
    var want = normalizeGiftCode(param);
    if (!want) return null;
    var hit = buildGiftPromoList().find(function (p) {
      return normalizeGiftCode(p.code) === want;
    });
    if (!hit) return null;
    if (!isGiftPromoActiveNow(hit)) return null;
    return {
      code: normalizeGiftCode(hit.code),
      variantId: hit.variantId,
      title: hit.title || 'Gift',
      image: hit.image || '',
      price: 0,
      qty: 1,
      isGift: true
    };
  }
  const ACTIVE_URL_GIFT = resolveUrlGiftPromo();
  const ACTIVE_URL_GIFT_CODE = ACTIVE_URL_GIFT ? ACTIVE_URL_GIFT.code : null;
  const GIFT_VARIANT_ID = ACTIVE_URL_GIFT ? ACTIVE_URL_GIFT.variantId : (DATA.giftVariantId || null);
  const GIFT_DATA = ACTIVE_URL_GIFT || {
    variantId: DATA.giftVariantId || null,
    title: DATA.giftProductTitle || 'Gift',
    image: DATA.giftProductImage || '',
    price: 0,
    qty: 1,
    isGift: true
  };
  const BMB_GIFT_ACTIVE = !!ACTIVE_URL_GIFT;

  // Gift BYOB mode: URL-only (?gifting=1). Not ?gift= (SUMMERWAFFLE) and
  // not ?view=gifting (preset PDP). No sessionStorage. A later visit
  // without the flag is the normal subscription builder.
  var BMB_GIFTING = false;
  try { BMB_GIFTING = new URLSearchParams(window.location.search).get('gifting') === '1'; } catch (e) {}
  if (BMB_GIFTING) {
    document.documentElement.classList.add('bmb-gifting');
  }

  function oneTimeGate() {
    var g = window.ATOME_ONE_TIME;
    if (!g || !g.allowed) return { mode: 'off', allowed: false, source: 'none' };
    return g;
  }
  function oneTimeAllowed() {
    if (BMB_GIFTING) return false;
    return !!oneTimeGate().allowed;
  }
  function effectivePlanId() {
    if (BMB_GIFTING) return null;
    if (STATE.sellingPlanId != null) return STATE.sellingPlanId;
    if (!oneTimeAllowed()) {
      return DEFAULT_SELLING_PLAN ? DEFAULT_SELLING_PLAN.id : null;
    }
    return null;
  }
  function isOneTimeSelected() {
    return !BMB_GIFTING && STATE.sellingPlanId == null && oneTimeAllowed();
  }

  // ── BOGO add-on deal (Sep 2026 r3) ───────────────────────
  // Merchant product_list from the section (BMB_DATA.bogo). Adding
  // writes STATE.extrasItems, never box slots. Gifts are DERIVED from
  // those extras on every render: each paid deal unit (cap 4) mirrors
  // one free unit of the SAME variant. Checkout pushes the gift as its
  // own plain cart line (properties._bogo_gift). Theme displays FREE;
  // the Shopify automatic Buy X Get Y discount zeroes the line.
  const BOGO = DATA.bogo || {};
  const BOGO_MAX_PAID = 4;
  const BOGO_PRODUCTS = (BOGO.products || []).map(function (p) {
    return {
      variantId: parseInt(p.variantId, 10),
      productId: parseInt(p.productId, 10) || 0,
      title: p.title || '',
      image: p.image || '',
      price: parseInt(p.price, 10) || 0,
      comparePrice: p.comparePrice || null,
      handle: p.handle || '',
      available: p.available !== false
    };
  }).filter(function (p) { return !isNaN(p.variantId) && p.variantId; });
  const BOGO_VARIANT_IDS = new Set(
    (BOGO_PRODUCTS.length ? BOGO_PRODUCTS.map(function (p) { return p.variantId; })
      : (BOGO.variantIds || []).map(function (v) { return parseInt(v, 10); }).filter(function (v) { return !isNaN(v); }))
  );
  const BOGO_ENDS_AT = (function () {
    if (!BOGO.endsAt) return null;
    var d = new Date(BOGO.endsAt);
    return isNaN(d.getTime()) ? null : d;
  })();
  function bogoActive() {
    if (BOGO.active === false) return false;
    if (BOGO_VARIANT_IDS.size === 0) return false;
    return !BOGO_ENDS_AT || BOGO_ENDS_AT.getTime() > Date.now();
  }
  function bogoProduct(variantId) {
    return BOGO_PRODUCTS.find(function (p) { return p.variantId === variantId; }) || null;
  }
  function isBogoExtra(item) {
    return !!item && !item.isGift && item.qty > 0 && bogoActive() && BOGO_VARIANT_IDS.has(item.variantId);
  }
  function bogoGifts() {
    return STATE.extrasItems.filter(isBogoExtra).map(function (i) {
      var meta = bogoProduct(i.variantId) || {};
      return {
        variantId: i.variantId,
        qty: i.qty,
        title: meta.title || i.title,
        titleEn: i.titleEn || meta.title || i.title,
        image: meta.image || i.image
      };
    });
  }
  function bogoCountdownText() {
    if (!BOGO_ENDS_AT) return '';
    var ms = BOGO_ENDS_AT.getTime() - Date.now();
    if (ms <= 0) return '';
    var total = Math.floor(ms / 1000);
    var days = Math.floor(total / 86400);
    var hrs = Math.floor((total % 86400) / 3600);
    var mins = Math.floor((total % 3600) / 60);
    var secs = total % 60;
    function pad(n) { return (n < 10 ? '0' : '') + n; }
    var clock = pad(hrs) + ':' + pad(mins) + ':' + pad(secs);
    return days > 0 ? days + ' ' + (days === 1 ? S.unitDay : S.unitDays) + ' ' + clock : clock;
  }
  // ────────────────────────────────────────────────────────

  var _rcInitDone = false;
  function initRechargeSDK() {
    if (_rcInitDone || !RC_TOKEN || !window.recharge) return;
    try {
      recharge.init({
        storefrontAccessToken: RC_TOKEN,
        storeIdentifier: (window.Shopify && window.Shopify.shop) ? window.Shopify.shop : '',
        appName: 'bmb-builder',
        appVersion: '1.0.0'
      });
      _rcInitDone = true;
    } catch (e) {
      console.warn('[BMB] Recharge SDK init failed:', e);
    }
  }
  initRechargeSDK();
  if (!_rcInitDone) {
    document.addEventListener('DOMContentLoaded', function () { setTimeout(initRechargeSDK, 500); });
  }

  // Default frequency (round 16, Aug 23 2026): this page is subscription
  // only. Default new sessions to the 30-day plan. SELLING_PLANS is
  // populated 30-day first, so prefer a plan whose name mentions "30",
  // falling back to the first configured plan. STATE.sellingPlanId is
  // never null: restoreStateFromSession()/restoreFromLocalStorage()
  // coerce a leftover saved null (old one-time) back to this default.
  var DEFAULT_SELLING_PLAN = SELLING_PLANS.find(function (p) { return /30/.test(p.name || ''); }) || SELLING_PLANS[0] || null;
  var _lastSubPlanId = DEFAULT_SELLING_PLAN ? DEFAULT_SELLING_PLAN.id : null;

  // Default tier: smallest configured size, the "website entry" BYB6
  // 6-slot tier per spec, unless a saved session/cart already picked a
  // tier (restoreStateFromSession/restoreFromLocalStorage below override
  // this).
  var SORTED_BOX_SIZES = BOX_SIZES.slice().sort(function (a, b) { return a.size - b.size; });
  var DEFAULT_TIER_SIZE = SORTED_BOX_SIZES.length ? SORTED_BOX_SIZES[0].size : 6;

  const STATE = {
    items: [],              // [{variantId, productId, collectionId, title, titleEn, image, qty, isGift}]
    extrasItems: [],        // [{variantId, qty, title, image, price, productId}] add-ons, not box slots
    extrasSearch: '',
    extrasDismissed: false, // persisted after extras close; builder/drawer CTAs ignore it
    selectedTier: DEFAULT_TIER_SIZE, // 6 | 10 | 12, matches a BOX_SIZES[].size (= item-slot capacity)
    sellingPlanId: BMB_GIFTING ? null : (DEFAULT_SELLING_PLAN ? DEFAULT_SELLING_PLAN.id : null),
    activeFilter: 'all',
    activePanel: null,     // null | 'drawer' | 'extras'
    searchQuery: '',
    // Pan state, mirrors bab-custom.js's STATE.panVariantId / isPanCastIron / panUserIntent.
    panVariantId: null,     // null = no pan in the box
    isPanCastIron: false,
    panQty: 1,              // Box pan is always 1. Extra pans live in extrasItems.
    panUserIntent: null,    // null | 'added' | 'removed': overrides the auto-include rule once set
    panJustAdded: false,    // brief confirmation after Yes on the pan prompt
    _clearBoxPending: false, // round 10 (Aug 16 2026), item 6: two-tap "Clear box" confirm state, transient (never persisted, see syncToGlobalBar)
    giftNote: ''            // gift mode only. Persisted with cart state; restored only when BMB_GIFTING.
  };
  var clearBoxConfirmTimer = null;

  // ── i18n bootstrap ──────────────────────────────────────
  refreshI18n();
  (function watchI18n() {
    var pending = null;
    function reapply() {
      clearTimeout(pending);
      pending = setTimeout(function () {
        if (refreshI18n()) { try { render(); } catch (e) {} }
      }, 60);
    }
    var observer = new MutationObserver(reapply);
    ['bmb-i18n', 'bmb-i18n-items', 'bmb-i18n-extras'].forEach(function (id) {
      var node = document.getElementById(id);
      if (node) observer.observe(node, { subtree: true, childList: true, characterData: true });
    });
    var tries = 0;
    (function hookWeglot() {
      if (window.Weglot && typeof Weglot.on === 'function') {
        Weglot.on('initialized', function () { setTimeout(reapply, 50); });
        Weglot.on('languageChanged', function () { setTimeout(reapply, 300); });
        if (window.Weglot.initialized) reapply();
      } else if (tries++ < 80) {
        setTimeout(hookWeglot, 250);
      }
    })();
    var lastLang = bmbLang();
    var pollCount = 0;
    var langPoll = setInterval(function () {
      var now = bmbLang();
      if (now !== lastLang) { lastLang = now; reapply(); }
      if (now !== 'en' || pollCount++ > 24) clearInterval(langPoll);
    }, 250);
  })();

  /* --------------------------------------------------------
     2. HELPERS
  -------------------------------------------------------- */
  function fmt(cents) {
    return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
  }
  function clamp(v, min, max) { return Math.max(min, Math.min(max, v)); }

  /* --------------------------------------------------------
     2a. BODY SCROLL LOCK MANAGER (scroll-lock audit, Aug 26 2026).
     Previously every overlay wrote document.body.style.overflow
     directly, with per-close conditionals guessing whether another
     overlay still needed the lock. This is the single authority now:
     each overlay owns a named key ('panel' for the review/extras
     drawers, 'modal' for the product info modal, 'shipcalc' for the
     shipping estimate popup), opening sets its key, closing clears
     its key, and the body is locked while ANY key is held. A close
     path can never underflow another overlay's lock, and re-opening
     the same overlay can never double-acquire.
  -------------------------------------------------------- */
  var _scrollLocks = {};
  function setScrollLock(key, on) {
    if (on) _scrollLocks[key] = true;
    else delete _scrollLocks[key];
    var any = false;
    for (var k in _scrollLocks) { if (Object.prototype.hasOwnProperty.call(_scrollLocks, k)) { any = true; break; } }
    document.body.style.overflow = any ? 'hidden' : '';
    if (any) document.body.classList.add('bmb-scroll-locked');
    else document.body.classList.remove('bmb-scroll-locked');
  }

  /* --------------------------------------------------------
     TIERS (round 15, Aug 18 2026): the box tier picked up-front. Tier
     size == item-slot capacity (6/10/12), 1 item qty = 1 slot, exactly
     bab-custom.js's bundleCount() vs STATE.selectedSize (no weighting).
  -------------------------------------------------------- */
  function currentTier() {
    return BOX_SIZES.find(function (b) { return b.size === STATE.selectedTier; }) || null;
  }
  function tierCapacity() {
    var t = currentTier();
    return t ? t.size : (STATE.selectedTier || 0);
  }
  // Absolute largest configured tier, the live builder's own absMax:
  // adding/incrementing is only ever blocked here, never at the current
  // (smaller) tier's own capacity, since crossing that just auto-upsizes
  // the tier (see autoSelectSize below).
  function absoluteMaxSize() {
    return SORTED_BOX_SIZES.length ? SORTED_BOX_SIZES[SORTED_BOX_SIZES.length - 1].size : (STATE.selectedTier || 0);
  }
  function boxIsFull() {
    return tierCapacity() > 0 && itemCount() === tierCapacity();
  }
  // Grow-only (round 16): if the current tier already fits `count`, keep
  // it. Only bump selectedTier when the shopper adds past the current
  // capacity. Manual selectTier() (and the shipcalc upgrade CTA) still
  // set size explicitly, including trimming on a manual downsize.
  // Previously this snapped to the SMALLEST fitting tier, which silently
  // downgraded a Medium/Large pick after the first add.
  function autoSelectSize(count) {
    if (!SORTED_BOX_SIZES.length) return;
    if ((STATE.selectedTier || 0) >= count) return;
    var fit = SORTED_BOX_SIZES.find(function (b) { return b.size >= count; });
    STATE.selectedTier = fit ? fit.size : SORTED_BOX_SIZES[SORTED_BOX_SIZES.length - 1].size;
  }
  // Tier price used for the shipping pill / footer total: the currently
  // active plan's price if a subscription is selected, else the tier's
  // one-time price. Exact Shopify allocation prices only, never re-derived.
  function currentPlan() {
    if (STATE.sellingPlanId === null) return null;
    return SELLING_PLANS.find(function (p) { return p.id === STATE.sellingPlanId; }) || null;
  }
  function currentDiscountPercent() {
    var plan = currentPlan();
    return plan ? (plan.discountPercent || 0) : 0;
  }
  // Size card shipping pill: found by [data-size-ship] (never by the fee
  // class), so the fee/free state and text toggle both ways on every render.
  function updateSizeShipPill(card, tier) {
    var shipEl = card.querySelector('[data-size-ship]');
    if (!shipEl) return;
    var shipThreshold = isOneTimeSelected() ? CART_FREE_SHIP_CENTS : FREE_SHIP_CENTS;
    var shipQualifies = tierPayPrice(tier) >= shipThreshold;
    if (shipQualifies) {
      shipEl.textContent = S.shipNode || 'Free shipping';
      shipEl.classList.remove('bmb-size-card__ship--fee');
    } else {
      shipEl.textContent = S.shippingFeeApplies || 'Shipping fee applies';
      shipEl.classList.add('bmb-size-card__ship--fee');
    }
    shipEl.hidden = false;
  }
  // Active selling-plan allocation only (round 16: subscription-only page,
  // never the one-time variant price). Falls back to the first allocation
  // on the tier, then 0, never tier.price.
  function planPriceForTier(tier) {
    if (!tier) return 0;
    if (BMB_GIFTING || isOneTimeSelected()) return tier.price || 0;
    var plans = tier.sellingPlans || [];
    if (STATE.sellingPlanId != null) {
      var alloc = plans.find(function (p) { return p.id === STATE.sellingPlanId; });
      if (alloc) return alloc.price;
    }
    if (plans[0]) return plans[0].price;
    return 0;
  }
  function tierDisplayPriceCents(tier) {
    return planPriceForTier(tier);
  }
  // Price the customer pays for this tier under the current plan (display +
  // free-shipping messaging). One-time / gift uses tier.price; subscription
  // uses the active selling-plan allocation (same as size-card sub prices).
  function tierPayPrice(tier) {
    return planPriceForTier(tier);
  }
  // Round 9 fix (Aug 16 2026): the raw Shopify selling-plan name for this
  // product reads "30 day subscription with 20% discount", which duplicated
  // the discount already shown by the separate green "SAVE 20%" badge next
  // to it. The live cart's own plans are named the simpler "Delivery every
  // 30 days" (bab-custom.js's localizePlanName comment confirms that's the
  // raw API string there), so extract just the day count from whatever
  // name this product's plan has and render the same live-style label,
  // letting the badge be the only place the discount is said.
  function planDeliveryLabelFull(plan) {
    if (!plan) return S.subscription;
    var m = (plan.name || '').match(/(\d+)\s*-?\s*days?/i);
    if (m) return S.deliveryEvery.replace('{n}', m[1]);
    return planDeliveryLabel(plan);
  }

  function planDeliveryLabel(plan) {
    if (!plan) return S.subscription;
    var m = (plan.name || '').match(/(\d+)\s*-?\s*days?/i);
    if (m) return S.deliveryEveryShort.replace('{n}', m[1]);
    return plan.name || S.subscription;
  }
  function itemCount() {
    return STATE.items.reduce(function (s, i) { return s + (i.isGift ? 0 : i.qty); }, 0);
  }
  // Pan price: a plain dollar add-on, entirely outside the box's own
  // item-slot pricing (the pan was never part of the a la carte item
  // pricing this builder replaced either).
  // Box Atome Pan is always qty 1 at the $10 promo. Extra pans stay in
  // extrasItems at catalog extra.price ($40). Do not merge them.
  function panPriceCents() {
    if (!STATE.panVariantId) return 0;
    if (STATE.isPanCastIron) return CAST_IRON ? CAST_IRON.price : 0;
    return PAN ? PAN.price : 1000;
  }
  // Footer TOTAL: the selected tier's price (sub or one-time, per the
  // frequency pill) plus the pan if present, never a sum of item sticker
  // prices -- items don't carry dollar prices in this model at all.
  // BOGO gifts are derived rows, not extrasItems, so they are not summed.
  function extrasTotalCents() {
    return STATE.extrasItems.reduce(function (s, i) {
      var extra = EXTRAS.find(function (e) { return e.variantId === i.variantId; });
      var deal = bogoProduct(i.variantId);
      var unit = extra ? extra.price : (deal ? deal.price : (i.price || 0));
      return s + unit * (i.qty || 0);
    }, 0);
  }
  function totalCents() {
    return tierDisplayPriceCents(currentTier()) + panPriceCents() + extrasTotalCents();
  }
  // Shipping messaging gate: tier pay price for the selected plan vs threshold,
  // not a live-computed cart total (per spec, "do not compute live Revy rates").
  function tierQualifiesFreeShipping() {
    var t = currentTier();
    if (!t) return false;
    var threshold = isOneTimeSelected() ? CART_FREE_SHIP_CENTS : FREE_SHIP_CENTS;
    return tierPayPrice(t) >= threshold;
  }

  /* --------------------------------------------------------
     PAN LOGIC (copied wholesale from bab-custom.js section 9 "PAN LOGIC")
     Conditional pan inclusion: the pan auto-includes only while the box
     holds at least one needs-pan item (bmb-breads minus pizza dough, plus
     brioche items in bmb-pastries, computed in bmb-builder.liquid and
     read here via NEEDS_PAN_VARIANT_IDS). Explicit user actions
     (setPan/removePan, wired to manual UI controls only) always set
     panUserIntent, which overrides the auto rule in that direction until
     the user takes the opposite explicit action.
  -------------------------------------------------------- */
  function setPan(castIron) {
    if (castIron && CAST_IRON) {
      STATE.panVariantId = CAST_IRON.variantId;
      STATE.isPanCastIron = true;
      STATE.panQty = 1;
    } else if (!castIron && PAN) {
      STATE.panVariantId = PAN.variantId;
      STATE.isPanCastIron = false;
      if (!STATE.panQty) STATE.panQty = 1;
    }
    STATE.panUserIntent = 'added';
    render();
  }

  function removePan() {
    STATE.panVariantId = null;
    STATE.isPanCastIron = false;
    STATE.panQty = 1;
    STATE.panUserIntent = 'removed';
    render();
  }

  // True while the box holds at least one item tagged needs-pan.
  function boxNeedsPan() {
    return STATE.items.some(function (i) { return !i.isGift && i.qty > 0 && NEEDS_PAN_VARIANT_IDS.has(i.variantId); });
  }

  function panChoiceOutstanding() {
    return boxNeedsPan() && STATE.panUserIntent === null;
  }

  function continueCtaLabel() {
    return (boxIsFull() && panChoiceOutstanding()) ? S.choosePanOption : S.continueToExtras;
  }

  function stickyFullBoxCtaLabel() {
    if (boxIsFull() && panChoiceOutstanding()) return S.choosePanOption;
    return S.reviewMyBox;
  }

  function openFullBoxFromSticky() {
    if (panChoiceOutstanding()) {
      showPanChoiceGate();
      return;
    }
    if (window.innerWidth < 960) {
      openDrawer();
      return;
    }
    openExtrasPanel();
  }

  function findVisiblePanPrompt() {
    var drawer = document.querySelector('.bmb-drawer--open .bmb-pan-prompt');
    if (drawer) return drawer;
    return document.querySelector('.bmb-sidebar .bmb-pan-prompt') || document.querySelector('.bmb-pan-prompt');
  }

  function scrollPromptInCart(prompt) {
    if (!prompt) return;
    var scroller = prompt.closest('.bmb-sidebar__body, .bmb-drawer__body');
    if (scroller) {
      var promptTop = prompt.getBoundingClientRect().top;
      var scrollerTop = scroller.getBoundingClientRect().top;
      var offset = promptTop - scrollerTop - Math.max(12, (scroller.clientHeight - prompt.offsetHeight) / 3);
      var nextTop = scroller.scrollTop + offset;
      if (typeof scroller.scrollTo === 'function') {
        scroller.scrollTo({ top: Math.max(0, nextTop), behavior: 'smooth' });
      } else {
        scroller.scrollTop = Math.max(0, nextTop);
      }
      return;
    }
    if (typeof prompt.scrollIntoView === 'function') {
      prompt.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  function showPanChoiceGate() {
    if (window.innerWidth < 960 && STATE.activePanel !== 'drawer') {
      openDrawer();
    }
    window.setTimeout(function () {
      var prompt = findVisiblePanPrompt();
      if (prompt) {
        prompt.classList.remove('bmb-pan-prompt--gate');
        void prompt.offsetWidth;
        prompt.classList.add('bmb-pan-prompt--gate');
        scrollPromptInCart(prompt);
      }
    }, 60);
  }

  // Prompt instead of silent auto-add: when the box first needs a pan and
  // the shopper has not answered, leave panVariantId null so buildPanBlock
  // can render the Yes / I have one card. Auto-remove still runs when the
  // last needs-pan item leaves, unless the user explicitly added a pan.
  function syncPanAutoInclude() {
    var needsPan = boxNeedsPan();
    if (needsPan && STATE.panVariantId === null && STATE.panUserIntent !== 'removed') {
      return;
    } else if (!needsPan && STATE.panVariantId !== null && STATE.panUserIntent !== 'added') {
      STATE.panVariantId = null;
      STATE.isPanCastIron = false;
      STATE.panJustAdded = false;
    } else if (!needsPan && STATE.panUserIntent === null) {
      STATE.panJustAdded = false;
    }
  }

  /* --------------------------------------------------------
     2b. META PIXEL : same eventID-dedup pattern as the live
     builder, renamed so any custom-event variant never collides
     with the live BABBuildBox naming.
  -------------------------------------------------------- */
  function bmbMetaEventId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return 'bmb-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }
  function bmbFireMetaCustom(eventName, params) {
    try {
      if (typeof window.fbq !== 'function') return;
      var eventId = bmbMetaEventId();
      window.fbq('trackCustom', eventName, params || {}, { eventID: eventId });
      if (window.console && console.debug) console.debug('[BMB Pixel]', eventName, params, '→ eventID:', eventId);
    } catch (err) {
      if (window.console && console.warn) console.warn('[BMB Pixel] failed:', err);
    }
  }

  /* --------------------------------------------------------
     3. DOM REFS
  -------------------------------------------------------- */
  const $ = id => document.getElementById(id);
  const el = {
    stickyContent: null,
    chooseSize: null, sidebarFreq: null, drawerFreq: null,
    backdrop: null, drawer: null, drawerBody: null, drawerFooter: null,
    drawer2: null, drawer2Body: null, drawer2Footer: null,
    productModal: null, giftNoteModal: null, loadingOverlay: null, panWarning: null,
    sidebarBody: null, sidebarFooter: null,
    sidebarHeadCount: null, drawerHeadCount: null,
    floatPill: null, floatPillText: null,
  };

  /* --------------------------------------------------------
     4. RENDER
  -------------------------------------------------------- */
  function render() {
    renderSizeCards();
    renderTopFreq();
    renderUnitPrices();
    renderCartFreq();
    renderProductCards();
    renderStickyBar();
    renderFloatingPill();
    renderHeadCounts();
    if (STATE.activePanel === 'drawer') renderDrawer();
    if (STATE.activePanel === 'extras') renderExtrasDrawer();
    renderDesktopSidebar();
    syncToGlobalBar();
    var fillNum = document.getElementById('bmb-fill-step-num');
    if (fillNum) fillNum.textContent = BMB_GIFTING ? '2' : (oneTimeAllowed() ? '3' : '2');
  }

  // Cart redesign (Aug 26 2026): the head-count "N / size items" spans
  // are gone from the sidebar and drawer heads. The count now lives in
  // the in-body "N of M items chosen" progress block (buildProgressHtml),
  // per the reference layout. Null guards keep this a safe no-op if a
  // cached head-count span is still around.
  function renderHeadCounts() {
    if (el.sidebarHeadCount) el.sidebarHeadCount.textContent = '';
    if (el.drawerHeadCount) el.drawerHeadCount.textContent = '';
  }

  function syncToGlobalBar() {
    try {
      // Storage key bumped to _v3 (round 15, Aug 18 2026): a leftover
      // credit-weighted cart under the old _v2 key must never silently
      // restore into this 1-item-1-slot model (mismatched item shapes:
      // old carts carry a per-item .credits field this model never reads).
      localStorage.setItem('atome_bmb_cart_v4', JSON.stringify({
        items: STATE.items,
        extrasItems: STATE.extrasItems,
        extrasDismissed: STATE.extrasDismissed,
        selectedTier: STATE.selectedTier,
        sellingPlanId: STATE.sellingPlanId,
        panVariantId: STATE.panVariantId,
        isPanCastIron: STATE.isPanCastIron,
        panQty: STATE.panQty || 1,
        panUserIntent: STATE.panUserIntent,
        giftNote: BMB_GIFTING ? (STATE.giftNote || '') : '',
        gifting: BMB_GIFTING,
        bmbPageUrl: window.location.pathname
      }));
    } catch (e) {}
    window.dispatchEvent(new CustomEvent('bmb:sync-to-global', { detail: STATE }));
  }

  /* --------------------------------------------------------
     STEP 1 · SIZE CARDS (round 17, Aug 23 2026): Liquid keeps the
     shells + data-size wiring. This toggles the selected fill and
     writes the ACTIVE plan's box price and SAVE % (vs the 6-credit
     unit price on the same plan) on every render() so they follow
     the 30/60 dropdown. Price line is box price only, no /credit.
  -------------------------------------------------------- */
  function renderSizeCards() {
    if (!el.chooseSize) return;
    var small = BOX_SIZES.find(function (b) { return b.size === 6; }) || SORTED_BOX_SIZES[0] || null;
    var smallUnit = (small && small.size) ? (planPriceForTier(small) / small.size) : 0;

    el.chooseSize.querySelectorAll('.bmb-size-card').forEach(function (card) {
      var size = parseInt(card.dataset.size, 10);
      var isActive = size === STATE.selectedTier;
      card.classList.toggle('bmb-size-btn--active', isActive);
      card.setAttribute('aria-pressed', isActive ? 'true' : 'false');

      var tier = BOX_SIZES.find(function (b) { return b.size === size; });
      if (!tier || !size) return;

      var boxPrice = planPriceForTier(tier);
      var priceEl = card.querySelector('[data-size-price]');
      if (priceEl) {
        priceEl.textContent = fmt(boxPrice);
      }

      updateSizeShipPill(card, tier);

      var badgePopular = card.querySelector('[data-size-badge="popular"]');
      if (badgePopular && S.popular) badgePopular.textContent = S.popular;
      var badgeBest = card.querySelector('[data-size-badge="best"]');
      if (badgeBest && S.bestValue) badgeBest.textContent = S.bestValue;

      var saveEl = card.querySelector('[data-size-save]');
      if (saveEl) {
        var pct = savePctVsSmall(size, smallUnit);
        if (DATA.showSavePercent !== false && pct > 0) {
          saveEl.textContent = S.save + ' ' + pct + '%';
          saveEl.hidden = false;
        } else {
          saveEl.textContent = '';
          saveEl.hidden = true;
        }
      }
    });
  }

  /* --------------------------------------------------------
     CART FREQUENCY (round 17, Aug 23 2026): subscription only.
     30/60 select from the same sellingPlans source, plus two
     reassurance pills, mounted in BOTH cart heads (sidebar + drawer).
     No one-time compare. Change handler is the existing
     [data-action="select-plan-freq"] listener below.
  -------------------------------------------------------- */
  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function buildGiftModeHeadHtml() {
    var note = String(STATE.giftNote || '').trim();
    var pill = '<span class="bmb-freq-pill bmb-freq-pill--otp">' + S.oneTimePurchase + '</span>';
    var noteCtrl = note
      ? ('<div class="bmb-gift-note-ctrl">' +
          '<button type="button" class="bmb-gift-note-link" data-action="edit-gift-note">' + S.giftNoteEdit + '</button>' +
          '<button type="button" class="bmb-gift-note-link" data-action="remove-gift-note">' + S.giftNoteRemove + '</button>' +
        '</div>')
      : '<button type="button" class="bmb-gift-note-link" data-action="open-gift-note">' + S.addGiftNote + '</button>';
    return (
      '<div class="bmb-freq-row bmb-freq-row--gifting">' +
        pill +
        noteCtrl +
      '</div>'
    );
  }

  function formatUnitLabel(label) {
    var raw = String(label == null ? '' : label).trim();
    if (!raw) return 'item';
    var rest = raw.slice(1);
    if (rest && rest !== rest.toLowerCase()) return raw;
    return raw.toLowerCase();
  }

  function localizeUnitLabel(label) {
    var formatted = formatUnitLabel(label);
    if (bmbLang() !== 'fr') return formatted;
    return UNIT_FR[formatted] || formatted;
  }

  function formatUnitPrice(cents) {
    var n = (cents / 100).toFixed(2);
    if (bmbLang() === 'fr') return n.replace('.', ',') + ' $';
    return '$' + n;
  }

  function renderUnitPrices() {
    if (DATA.showUnitPrice === false) {
      document.querySelectorAll('.bmb-product-card [data-unit-price]').forEach(function (el) { el.textContent = ''; el.hidden = true; });
      return;
    }
    var tier = currentTier();
    if (!tier || !tier.size) return;
    var perCredit = planPriceForTier(tier) / tier.size;
    document.querySelectorAll('.bmb-product-card [data-unit-price]').forEach(function (el) {
      var card = el.closest('.bmb-product-card');
      var units = card ? parseFloat(card.getAttribute('data-servings')) : 0;
      var price;
      if (units > 0) {
        price = formatUnitPrice(perCredit / units);
        el.textContent = (S.perUnit || '{price} per {unit}')
          .replace('{price}', price)
          .replace('{unit}', localizeUnitLabel(card.getAttribute('data-unit-label')));
      } else {
        price = formatUnitPrice(perCredit);
        el.textContent = (S.inABox || '{price} in a box').replace('{price}', price);
      }
    });
  }

  function buildTopFreqCards() {
    var tier = currentTier();
    if (!tier) return '';
    var plans = SELLING_PLANS.length ? SELLING_PLANS : (tier.sellingPlans || []);
    if (!plans.length) return '';
    var subActive = STATE.sellingPlanId != null;
    var plan = plans.find(function (p) { return p.id === (STATE.sellingPlanId || _lastSubPlanId); }) || plans[0];
    var subPrice = planPriceForTier(tier);
    if (STATE.sellingPlanId == null) {
      var alloc = (tier.sellingPlans || []).find(function (p) { return p.id === plan.id; });
      subPrice = alloc ? alloc.price : Math.round((tier.price || 0) * 0.8);
    }
    var oneTimePrice = tier.price || 0;
    var savings = oneTimePrice - subPrice;
    var pct = currentDiscountPercent() || (plan && plan.discountPercent) || 20;

    var freq = plans.length > 1
      ? '<select class="bmb-plan-card__freq" data-action="select-plan-freq" aria-label="' + S.deliveryPlan + '">' +
          plans.map(function (p) {
            return '<option value="' + p.id + '"' + (p.id === plan.id ? ' selected' : '') + '>' + planDeliveryLabelFull(p) + '</option>';
          }).join('') +
        '</select>'
      : '<span class="bmb-plan-card__freq bmb-plan-card__freq--static">' + planDeliveryLabelFull(plan) + '</span>';

    return (
      '<div class="bmb-plan-cards" role="radiogroup" aria-label="' + S.deliveryPlan + '">' +
        '<div class="bmb-plan-card bmb-plan-card--sub' + (subActive ? ' bmb-plan-card--active' : '') + '"' +
             ' role="radio" aria-checked="' + subActive + '" tabindex="0" data-plan-choice="' + plan.id + '">' +
          '<span class="bmb-plan-card__tag">' + S.mostPopular + '</span>' +
          '<div class="bmb-plan-card__head">' +
            '<span class="bmb-plan-card__radio" aria-hidden="true"></span>' +
            '<span class="bmb-plan-card__title">' + S.subscribeSave + '</span>' +
            (savings > 0 ? '<span class="bmb-plan-card__badge">' + S.save + ' ' + fmt(savings) + '</span>' : '') +
          '</div>' +
          '<div class="bmb-plan-card__price-row">' +
            '<span class="bmb-plan-card__price">' + fmt(subPrice) + '</span>' +
            (savings > 0 ? '<s class="bmb-plan-card__compare">' + fmt(oneTimePrice) + '</s>' : '') +
            '<span class="bmb-plan-card__unit">' + S.perBox + '</span>' +
            freq +
          '</div>' +
          '<ul class="bmb-plan-card__perks">' +
            '<li>' + S.skipCancelAnytime + '</li>' +
            '<li>' + (S.subPerkDiscount || '{pct}% off every box').replace('{pct}', String(pct)) + '</li>' +
            '<li>' + S.noCommitment + '</li>' +
          '</ul>' +
        '</div>' +
        '<div class="bmb-plan-card bmb-plan-card--once' + (!subActive ? ' bmb-plan-card--active' : '') + '"' +
             ' role="radio" aria-checked="' + (!subActive) + '" tabindex="0" data-plan-choice="onetime">' +
          '<div class="bmb-plan-card__head">' +
            '<span class="bmb-plan-card__radio" aria-hidden="true"></span>' +
            '<span class="bmb-plan-card__title">' + S.buyOnce + '</span>' +
            '<span class="bmb-plan-card__once-col">' +
              '<span class="bmb-plan-card__once-price">' + fmt(oneTimePrice) + '</span>' +
            '</span>' +
          '</div>' +
          '<p class="bmb-plan-card__once-note">' + S.noSubscription + '</p>' +
        '</div>' +
      '</div>'
    );
  }

  function renderTopFreq() {
    var step = document.querySelector('.bmb-choose__step--freq');
    var hideStep = BMB_GIFTING || !oneTimeAllowed();
    if (step) step.classList.toggle('bmb-hidden', hideStep);
    var c = document.getElementById('bmb-top-freq');
    if (!c) return;
    if (hideStep) {
      c.innerHTML = '';
      return;
    }
    c.innerHTML = buildTopFreqCards();
  }

  function selectPlanChoice(choice) {
    var newPlan = choice === 'onetime' ? null : parseInt(choice, 10);
    if (newPlan === STATE.sellingPlanId) return;
    STATE.sellingPlanId = newPlan;
    if (newPlan != null) _lastSubPlanId = newPlan;
    render();
  }

  function buildCartFreqHtml() {
    if (BMB_GIFTING) return buildGiftModeHeadHtml();
    if (isOneTimeSelected()) {
      return (
        '<div class="bmb-freq-row">' +
          '<span class="bmb-freq-pill bmb-freq-pill--otp">' + S.oneTimePurchase + '</span>' +
          '<button type="button" class="bmb-switch-to-sub" data-action="switch-to-sub">' + S.switchToSub + '</button>' +
        '</div>'
      );
    }
    var plans = SELLING_PLANS.length ? SELLING_PLANS : ((currentTier() && currentTier().sellingPlans) || []);
    if (!plans.length) return '';
    var currentId = effectivePlanId();
    var plan = plans.find(function (p) { return p.id === currentId; }) || plans[0];

    var freq = plans.length > 1
      ? '<select class="bmb-freq-select" data-action="select-plan-freq" aria-label="' + S.deliveryPlan + '">' +
          plans.map(function (p) {
            return '<option value="' + p.id + '"' + (p.id === plan.id ? ' selected' : '') + '>' + planDeliveryLabel(p) + '</option>';
          }).join('') +
        '</select>'
      : '<span class="bmb-freq-select bmb-freq-select--static">' + planDeliveryLabel(plan) + '</span>';

    return (
      '<div class="bmb-freq-row">' +
        freq +
        '<div class="bmb-freq-pills">' +
          '<span class="bmb-freq-pill">' + S.noCommitment + '</span>' +
          '<span class="bmb-freq-pill">' + S.cancelAnytime + '</span>' +
        '</div>' +
      '</div>'
    );
  }

  function renderCartFreq() {
    var html = buildCartFreqHtml();
    if (el.sidebarFreq) el.sidebarFreq.innerHTML = html;
    if (el.drawerFreq) el.drawerFreq.innerHTML = html;
  }

  document.addEventListener('change', function (e) {
    var sel = e.target.closest && e.target.closest('[data-action="select-plan-freq"]');
    if (!sel) return;
    var newPlan = parseInt(sel.value, 10);
    if (!newPlan) return;
    STATE.sellingPlanId = newPlan;
    _lastSubPlanId = newPlan;
    render();
  });
  document.addEventListener('click', function (e) {
    var switchBtn = e.target.closest && e.target.closest('[data-action="switch-to-sub"]');
    if (switchBtn) {
      selectPlanChoice(String(_lastSubPlanId));
      return;
    }
    var planCard = e.target.closest && e.target.closest('.bmb-plan-card');
    if (planCard && planCard.dataset.planChoice) {
      if (e.target.closest('[data-action="select-plan-freq"]')) return;
      selectPlanChoice(planCard.dataset.planChoice);
      return;
    }
    var stickyUp = e.target.closest && e.target.closest('[data-action="sticky-upgrade"]');
    if (!stickyUp) return;
    var nextSize = parseInt(stickyUp.getAttribute('data-size'), 10);
    if (nextSize) selectTier(nextSize);
  });
  document.addEventListener('click', function (e) {
    if (!e.target.closest || !e.target.closest('[data-action="footer-upgrade-10"]')) return;
    selectTier(10);
    closeAllPanels();
    scrollToFillGrid();
  });
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    var planCard = e.target.closest && e.target.closest('.bmb-plan-card');
    if (!planCard || !planCard.dataset.planChoice) return;
    if (e.target.closest('[data-action="select-plan-freq"]')) return;
    e.preventDefault();
    selectPlanChoice(planCard.dataset.planChoice);
  });

  /* --- product cards: qty controls only, live-style (round 15, Aug 18
     2026). No per-item overflow note/upgrade link: crossing the current
     tier's size just auto-upsizes the tier (autoSelectSize, called from
     addItem/changeItemQty), so the only real block left is the absolute
     largest configured tier, exactly bab-custom.js's own absMax rule. --- */
  function renderProductCards() {
    var absMax = absoluteMaxSize();
    var maxReached = itemCount() >= absMax;
    document.querySelectorAll('.bmb-product-card').forEach(function (card) {
      if (card.dataset.comingSoon === 'true') {
        if (S.comingSoon) card.querySelectorAll('[data-i18n-pill="comingSoon"]').forEach(function (n) { n.textContent = S.comingSoon; });
        return;
      }
      if (card.dataset.available === 'false') return;

      var variantId = parseInt(card.dataset.variantId, 10);
      var stateItem = STATE.items.find(function (i) { return i.variantId === variantId && !i.isGift; });
      var qty = stateItem ? stateItem.qty : 0;
      var atbBtn = card.querySelector('.bmb-atb-btn');
      var qtyWrap = card.querySelector('.bmb-qty');
      var qtyVal = card.querySelector('.bmb-qty__val');
      var plusBtn = card.querySelector('.bmb-qty__btn--plus');

      card.classList.toggle('bmb-product-card--in-box', qty > 0);

      if (qty === 0) {
        if (atbBtn) { atbBtn.classList.remove('bmb-hidden'); atbBtn.disabled = maxReached; }
        if (qtyWrap) qtyWrap.classList.add('bmb-hidden');
      } else {
        if (atbBtn) atbBtn.classList.add('bmb-hidden');
        if (qtyWrap) qtyWrap.classList.remove('bmb-hidden');
        if (qtyVal) qtyVal.textContent = qty;
        if (plusBtn) plusBtn.disabled = maxReached;
      }
    });
  }

  /* --- sticky bottom bar (mobile): CTA only. Remaining > 0 is
     "Add {n} items"; full under 960 is "Review my box" (opens Your Box
     drawer); drawer Continue then opens extras. Desktop sticky hidden.
     At 0 items the button scrolls to the fill grid instead of opening
     an empty cart. Optional upgrade pill when a larger tier exists. --- */
  function savePctVsSmall(size, smallUnitOpt) {
    var small = BOX_SIZES.find(function (b) { return b.size === 6; }) || SORTED_BOX_SIZES[0] || null;
    var smallUnit = (smallUnitOpt != null) ? smallUnitOpt : ((small && small.size) ? (planPriceForTier(small) / small.size) : 0);
    var tier = BOX_SIZES.find(function (b) { return b.size === size; });
    if (!tier || !size || !(smallUnit > 0) || size === 6) return 0;
    return Math.round((1 - (planPriceForTier(tier) / size) / smallUnit) * 100);
  }
  function nextUpgradeSize() {
    var current = STATE.selectedTier;
    if (current === 6 && BOX_SIZES.some(function (b) { return b.size === 10; })) return 10;
    if (current === 10 && BOX_SIZES.some(function (b) { return b.size === 12; })) return 12;
    return 0;
  }
  function buildStickyUpgradeHtml() {
    var next = nextUpgradeSize();
    if (!next) return '';
    var pct = savePctVsSmall(next);
    if (pct <= 0) return '';
    var label = S.upgradeToAndSave.replace('{n}', next).replace('{pct}', pct);
    return '<button type="button" class="bmb-sticky-card__upgrade" data-action="sticky-upgrade" data-size="' + next + '">' + label + '</button>';
  }
  function buildStickyContentHtml() {
    var cnt = itemCount();
    var capacity = tierCapacity();
    var full = boxIsFull();
    var left = Math.max(0, capacity - cnt);
    var label = full
      ? stickyFullBoxCtaLabel()
      : (left === 1 ? S.addStickyItem : S.addStickyItems).replace('{n}', left);
    var btnClass = 'bmb-sticky-card__btn' + (full ? '' : ' bmb-sticky-card__btn--incomplete');
    return (
      '<div class="bmb-sticky-card">' +
        buildStickyUpgradeHtml() +
        '<button class="' + btnClass + '" type="button" id="bmb-sticky-btn">' + label + '</button>' +
      '</div>'
    );
  }
  function scrollToFillGrid() {
    var target = document.getElementById('bmb-fill-step-label') || document.getElementById('bmb-grid');
    if (!target) return;
    // Empty-slot buttons sit in the overflow:auto sidebar. scrollIntoView
    // walks every ancestor, so it can scroll that sidebar (and its
    // overflow:hidden parent) instead of the window, then trap wheel/trackpad.
    if (document.activeElement && typeof document.activeElement.blur === 'function') {
      document.activeElement.blur();
    }
    var headerOffset = 88;
    var top = target.getBoundingClientRect().top + (window.pageYOffset || document.documentElement.scrollTop || 0) - headerOffset;
    if (window.scrollTo) window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }
  function renderStickyBar() {
    if (!el.stickyContent) return;
    el.stickyContent.innerHTML = buildStickyContentHtml();
    var btn = el.stickyContent.querySelector('#bmb-sticky-btn');
    if (btn) {
      btn.addEventListener('click', function () {
        if (itemCount() === 0) { scrollToFillGrid(); return; }
        if (boxIsFull()) { openFullBoxFromSticky(); return; }
        openDrawer();
      });
    }
  }

  // Site-wide floating pill (#bab-float-pill from bab-floating-cart.liquid):
  // always hidden on this page. The sticky CTA already says Add N / Continue,
  // so the black float count is redundant and covers the sticky. bab-* is
  // not edited; hide from here plus #bab-float-pill { display:none } in CSS.
  function renderFloatingPill() {
    if (!el.floatPill) return;
    el.floatPill.style.display = 'none';
  }

  /* --------------------------------------------------------
     5. CART ITEM ROW BUILDERS
  -------------------------------------------------------- */

  /* Optional event space (cart redesign, Aug 26 2026): clock icon +
     merchant label, plus a live "Order within X days Y hrs" countdown
     when event_deadline is set. Hidden entirely when event_label is
     empty or the deadline has passed; no reserved space either way. */
  function eventCountdownText() {
    if (!EVENT_DEADLINE) return '';
    var ms = EVENT_DEADLINE.getTime() - Date.now();
    if (ms <= 0) return '';
    var mins = Math.floor(ms / 60000);
    var days = Math.floor(mins / 1440);
    var hrs = Math.floor((mins % 1440) / 60);
    var m = mins % 60;
    var parts = [];
    if (days > 0) {
      parts.push(days + ' ' + (days === 1 ? S.unitDay : S.unitDays));
      parts.push(hrs + ' ' + (hrs === 1 ? S.unitHr : S.unitHrs));
    } else if (hrs > 0) {
      parts.push(hrs + ' ' + (hrs === 1 ? S.unitHr : S.unitHrs));
      parts.push(m + ' ' + S.unitMin);
    } else {
      parts.push(m + ' ' + S.unitMin);
    }
    return S.orderWithin.replace('{time}', parts.join(' '));
  }
  function eventExpired() {
    return !!EVENT_DEADLINE && EVENT_DEADLINE.getTime() <= Date.now();
  }
  function buildEventHtml() {
    if (!EVENT_LABEL || eventExpired()) return '';
    var countdown = eventCountdownText();
    return (
      '<div class="bmb-event" role="note">' +
        '<svg class="bmb-event__icon" viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
          '<circle cx="8" cy="8" r="6.2"></circle>' +
          '<path d="M8 4.8V8l2.2 1.5"></path>' +
        '</svg>' +
        '<div class="bmb-event__text">' +
          '<span class="bmb-event__label">' + EVENT_LABEL + '</span>' +
          (countdown ? '<span class="bmb-event__count" data-bmb-countdown>' + countdown + '</span>' : '') +
        '</div>' +
      '</div>'
    );
  }

  /* Item counter + slim progress bar (cart redesign, Aug 26 2026):
     "N of M items chosen" near the top of the cart. Absorbs the old
     head-count "N / M items" display; re-counts on every render,
     tier switches included. */
  function buildProgressHtml() {
    var cnt = itemCount();
    var capacity = tierCapacity();
    var pct = capacity > 0 ? clamp(Math.round((cnt / capacity) * 100), 0, 100) : 0;
    return (
      '<div class="bmb-cart-progress' + (cnt >= capacity && capacity > 0 ? ' bmb-cart-progress--full' : '') + '">' +
        '<span class="bmb-cart-progress__text">' + S.itemsChosen.replace('{n}', cnt).replace('{t}', capacity) + '</span>' +
        '<div class="bmb-cart-progress__bar" role="progressbar" aria-valuemin="0" aria-valuemax="' + capacity + '" aria-valuenow="' + cnt + '">' +
          '<div class="bmb-cart-progress__fill" style="width:' + pct + '%"></div>' +
        '</div>' +
      '</div>'
    );
  }

  /* Empty slot rows (cart redesign, Aug 26 2026): one dashed-circle row
     per remaining slot. qty 2 on one item = 2 slots consumed = 2 fewer
     empty rows. Capped: past 7 remaining, 6 rows render plus a
     "+N more slots" row so the Large tier never towers. The last visible
     slot fades, matching the reference. Rows scroll back to the fill
     grid on click (closing the drawer first on mobile). */
  var BMB_MAX_EMPTY_SLOT_ROWS = 7;
  function buildEmptySlotsHtml() {
    var remaining = Math.max(0, tierCapacity() - itemCount());
    if (remaining === 0) return '';
    var visible = remaining > BMB_MAX_EMPTY_SLOT_ROWS ? BMB_MAX_EMPTY_SLOT_ROWS - 1 : remaining;
    var overflow = remaining - visible;
    var html = [];
    for (var i = 0; i < visible; i++) {
      var fades = (i === visible - 1) && overflow === 0 && visible > 1;
      html.push(
        '<button type="button" class="bmb-slot' + (fades ? ' bmb-slot--fade' : '') + '" data-action="fill-slot">' +
          '<span class="bmb-slot__circle" aria-hidden="true">' +
            '<svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true"><path d="M8 3.5v9M3.5 8h9"></path></svg>' +
          '</span>' +
          '<span class="bmb-slot__label">' + S.emptySlot + '</span>' +
        '</button>'
      );
    }
    if (overflow > 0) {
      html.push(
        '<button type="button" class="bmb-slot bmb-slot--more" data-action="fill-slot">' +
          '<span class="bmb-slot__label">' + S.moreSlots.replace('{n}', overflow) + '</span>' +
        '</button>'
      );
    }
    return html.join('');
  }

  function buildCartItem(item) {
    if (item.isGift) {
      return '<div class="bmb-cart-item" data-variant-id="' + item.variantId + '">' +
        '<div class="bmb-cart-item__img">' + (item.image ? '<img src="' + item.image + '" alt="' + item.title + '" width="48" height="48" loading="lazy">' : '') + '</div>' +
        '<div class="bmb-cart-item__info">' +
          '<p class="bmb-cart-item__name">🎁 ' + item.title + '</p>' +
          '<p class="bmb-cart-item__price"><span class="bmb-free-tag">' + S.free + '</span></p>' +
        '</div>' +
        '<div class="bmb-cart-item__right"><span class="bmb-cart-item__note">' + S.included + '</span></div>' +
      '</div>';
    }
    return '<div class="bmb-cart-item" data-variant-id="' + item.variantId + '">' +
      '<div class="bmb-cart-item__img">' + (item.image ? '<img src="' + item.image + '" alt="' + item.title + '" width="48" height="48" loading="lazy">' : '') + '</div>' +
      '<div class="bmb-cart-item__info">' +
        '<p class="bmb-cart-item__name">' + item.title + '</p>' +
      '</div>' +
      '<div class="bmb-cart-item__right">' +
        '<button class="bmb-cart-item__remove" data-action="remove-item" data-variant-id="' + item.variantId + '" aria-label="' + S.remove + '">✕</button>' +
        '<div class="bmb-qty">' +
          '<button class="bmb-qty__btn bmb-qty__btn--minus" data-action="item-minus" data-variant-id="' + item.variantId + '">−</button>' +
          '<span class="bmb-qty__val">' + item.qty + '</span>' +
          '<button class="bmb-qty__btn bmb-qty__btn--plus" data-action="item-plus" data-variant-id="' + item.variantId + '">+</button>' +
        '</div>' +
      '</div>' +
    '</div>';
  }

  // BOGO gift row (Sep 2026 r3): rendered right under its paid extra,
  // same skeleton as the URL-gift row above (🎁 + FREE tag), plus the
  // merchant note and a mirrored "× N" instead of controls: the gift
  // follows the paid extra's own +/−/✕ and has none of its own.
  function buildBogoGiftRow(item) {
    return '<div class="bmb-cart-item bmb-cart-item--bogo-gift" data-bogo-gift-of="' + item.variantId + '">' +
      '<div class="bmb-cart-item__img">' + (item.image ? '<img src="' + item.image + '" alt="" width="48" height="48" loading="lazy">' : '') + '</div>' +
      '<div class="bmb-cart-item__info">' +
        '<p class="bmb-cart-item__name">🎁 ' + item.title + '</p>' +
        '<p class="bmb-cart-item__price"><span class="bmb-free-tag">' + S.free + '</span><span class="bmb-cart-item__bogo-note">' + S.bogoNote + '</span></p>' +
      '</div>' +
      '<div class="bmb-cart-item__right"><span class="bmb-cart-item__note bmb-cart-item__note--bogo">\u00d7 ' + item.qty + '</span></div>' +
    '</div>';
  }

  // Truth and Reconciliation Day gift row (Sep 30 2026): DISPLAY ONLY.
  // AtomeTRC.ensure() adds the real line right before checkout; this row
  // just shows it in the box panel. Not in STATE, so it never touches the
  // count, credits, tier, subtotal, selection payload or renewals.
  // Same gate as the promo: html.trc-active (set by snippets/trc-promo
  // only inside the window, kill switch, or ?trc_preview=1 on an
  // unpublished theme) + config + trc-gift.js loaded. END is re-checked so
  // a tab left open past the window drops the row on its next render.
  function trcGiftActive() {
    var cfg = window.ATOME_TRC;
    if (!cfg || !cfg.enabled || !window.AtomeTRC) return false;
    if (!document.documentElement.classList.contains('trc-active')) return false;
    var preview = false;
    try {
      var role = window.Shopify && Shopify.theme && Shopify.theme.role;
      preview = !!role && role !== 'main' && sessionStorage.getItem('trc_preview') === '1';
    } catch (e) {}
    var end = Date.parse(cfg.end);
    return preview || isNaN(end) || Date.now() < end;
  }

  function buildTrcGiftRow() {
    if (!trcGiftActive()) return '';
    var cfg = window.ATOME_TRC;
    var fallback = bogoProduct(parseInt(cfg.variantId, 10)) || {};
    var image = cfg.giftImage || fallback.image || '';
    var price = parseInt(cfg.giftPrice, 10) || fallback.price || 1899;
    return '<div class="bmb-cart-item bmb-cart-item--trc-gift" data-trc-gift-row>' +
      '<div class="bmb-cart-item__img">' + (image ? '<img src="' + escapeHtml(image) + '" alt="" width="48" height="48" loading="lazy">' : '') + '</div>' +
      '<div class="bmb-cart-item__info">' +
        '<p class="bmb-cart-item__name">' + S.trcGiftTitle + '</p>' +
        '<p class="bmb-cart-item__trc-label">' + S.trcGiftLabel + '</p>' +
        '<p class="bmb-cart-item__price"><s>' + fmt(price) + '</s><span class="bmb-cart-item__trc-free">' + S.free + '</span></p>' +
      '</div>' +
    '</div>';
  }

  // Pan block: copied from bab-custom.js's buildPanBlock(), adapted to
  // bmb- classes/i18n keys. Same three states as the live cart: pan in the
  // box (regular or cast-iron, with the info toggle + upgrade/remove
  // actions), pan explicitly removed (re-add link with the disclaimer), or
  // pan simply not needed yet (renders nothing, no unwarranted disclaimer).
  function buildPanBlock() {
    var panData = STATE.isPanCastIron ? CAST_IRON : PAN;
    if (!panData) return '';
    var hasPan = STATE.panVariantId !== null;
    var needsPan = boxNeedsPan();

    if (!hasPan && needsPan && STATE.panUserIntent === null) {
      return (
        '<div class="bmb-pan-prompt" role="group" aria-label="' + S.panPromptHeadline + '">' +
          '<p class="bmb-pan-prompt__headline">' + S.panPromptHeadline + '</p>' +
          '<p class="bmb-pan-prompt__support">' + S.panPromptSupport + '</p>' +
          '<div class="bmb-pan-prompt__actions">' +
            '<button class="bmb-cta-btn" type="button" data-action="pan-prompt-yes">' + S.panPromptYes + '</button>' +
            '<button class="bmb-cta-btn bmb-cta-btn--outline" type="button" data-action="pan-prompt-no">' + S.panPromptNo + '</button>' +
          '</div>' +
        '</div>'
      );
    }

    if (!hasPan) {
      if (STATE.panUserIntent !== 'removed') return '';
      return (
        '<div style="padding:14px 18px;border-bottom:1px solid var(--bmb-border);">' +
          '<p style="font-size:0.8125rem;color:var(--bmb-muted);font-style:italic;margin:0 0 8px;">' + S.panPromptReadd + '</p>' +
          '<button class="bmb-cta-btn bmb-cta-btn--outline" id="bmb-pan-readd" style="font-size:0.875rem;padding:10px;">' +
            S.addTheAtomePan + ' · ' + fmt(PAN ? PAN.price : 1000) + ' <s style="font-weight:400;">' + fmt(PAN ? PAN.comparePrice : 4000) + '</s>' +
          '</button>' +
        '</div>'
      );
    }

    return (
      '<div class="bmb-cart-section-label" style="display:flex;align-items:center;gap:5px;">' +
        '<span>' + S.yourBakingPan + '</span>' +
        '<button class="bmb-pan-info-btn" data-action="toggle-pan-info" type="button" aria-label="' + S.whyNeedPan + '">?</button>' +
      '</div>' +
      (STATE.panJustAdded ? '<p class="bmb-pan-prompt__added">' + S.panPromptAdded + '</p>' : '') +
      '<div class="bmb-pan-info-panel" style="display:none;">' +
        '<p class="bmb-pan-info-panel__title">' + S.whyNeedPanTitle + '</p>' +
        '<ul class="bmb-pan-info-list">' +
          '<li><span class="bmb-pan-info-list__name">' + S.panSteamTrap + '</span><span class="bmb-pan-info-list__desc">' + S.panSteamTrapDesc + '</span></li>' +
          '<li><span class="bmb-pan-info-list__name">' + S.panMadeForAtome + '</span><span class="bmb-pan-info-list__desc">' + S.panMadeForAtomeDesc + '</span></li>' +
          '<li><span class="bmb-pan-info-list__name">' + S.panWithout + '</span><span class="bmb-pan-info-list__desc">' + S.panWithoutDesc + '</span></li>' +
          '<li><span class="bmb-pan-info-list__name">' + S.panLightOrPremium + '</span><span class="bmb-pan-info-list__desc">' + S.panLightOrPremiumDesc + '</span></li>' +
        '</ul>' +
      '</div>' +
      '<div class="bmb-pan-item bmb-pan-item--compact" id="bmb-pan-block">' +
        '<div class="bmb-pan-item__img">' + (panData.image ? '<img src="' + panData.image + '" alt="' + panData.title + '" width="52" height="52" loading="lazy">' : '') + '</div>' +
        '<div class="bmb-pan-item__content">' +
          '<p class="bmb-pan-item__name">' + panData.title + ' ' +
            ((STATE.isPanCastIron && CAST_IRON_NO_DISCOUNT)
              ? '<strong>' + fmt(panData.price) + '</strong>'
              : '<s>' + fmt(panData.comparePrice) + '</s> <strong>' + fmt(panData.price) + '</strong> <span class="bmb-free-tag bmb-free-tag--inline">' + S.save + ' ' + fmt(panData.comparePrice - panData.price) + '</span>') +
          '</p>' +
          (boxNeedsPan() ? '<p style="font-size:0.75rem;color:var(--bmb-muted);margin:2px 0 6px;">' + S.panNeededForBreads + '</p>' : '') +
          '<div class="bmb-pan-item__actions">' +
            (!STATE.isPanCastIron && CAST_IRON
              ? '<button class="bmb-pan-upgrade-btn" id="bmb-pan-upgrade">' + S.upgradeToCastIron + ' · ' + fmt(CAST_IRON.price) + '</button>'
              : (STATE.isPanCastIron ? '<button class="bmb-pan-upgrade-btn" id="bmb-pan-downgrade">' + S.switchToAluminium + '</button>' : '')) +
            '<button class="bmb-pan-remove-btn" id="bmb-pan-remove">' + S.remove + '</button>' +
          '</div>' +
        '</div>' +
      '</div>'
    );
  }

  // Cart body order (round 6, Aug 16 2026): pan block moved to the TOP,
  // mirroring the live cart's own order exactly (renderCart1 in
  // bab-custom.js pushes buildPanBlock() first, before gifts and "Your
  // selections"). Was previously last; Lucas flagged it as too far down
  // and too large. The pan card's own CSS was already the compact
  // .bmb-pan-item--compact treatment (byte-identical to the live cart's),
  // so only the position needed to change here.
  // Cart redesign (Aug 26 2026): optional event space + "N of M items
  // chosen" progress live at the very top, then pan/gift/selections as
  // before, then one dashed "Empty slot" row per remaining slot (the
  // old 🧺 empty-state card is absorbed by the slot rows: an empty box
  // is just N empty slots now).
  function buildCartBody() {
    var html = [];
    html.push(buildEventHtml());
    html.push(buildProgressHtml());
    html.push(buildPanBlock());
    html.push(buildGiftNoteRow());
    STATE.items.forEach(function (item) { if (item.isGift) html.push(buildCartItem(item)); });
    var realItems = STATE.items.filter(function (i) { return !i.isGift; });
    // Round 10 (Aug 16 2026), item 6: discreet "Clear box" link, right-
    // aligned next to the section label, only shown when there's actually
    // something to clear (real items or a pan already in the box).
    var hasClearable = realItems.length > 0 || !!STATE.panVariantId;
    html.push(
      '<div class="bmb-cart-section-label bmb-cart-section-label--row">' +
        '<span>' + S.yourSelections + '</span>' +
        (hasClearable
          ? '<button type="button" class="bmb-clear-box-btn' + (STATE._clearBoxPending ? ' bmb-clear-box-btn--confirm' : '') + '" data-action="clear-box">' +
              (STATE._clearBoxPending ? S.clearBoxConfirm : S.clearBox) +
            '</button>'
          : '') +
      '</div>'
    );
    realItems.forEach(function (item) { html.push(buildCartItem(item)); });
    html.push(buildEmptySlotsHtml());
    html.push(buildTrcGiftRow());
    html.push(buildExtrasCartSection());
    return html.join('');
  }

  function extraCatalog(item) {
    return EXTRAS.find(function (e) { return e.variantId === item.variantId; }) || bogoProduct(item.variantId) || null;
  }

  function buildExtrasCartItem(item) {
    var extra = extraCatalog(item);
    var title = (extra && extra.title) || item.title || '';
    var image = (extra && extra.image) || item.image || '';
    var unit = extra ? extra.price : (item.price || 0);
    return '<div class="bmb-cart-item" data-variant-id="' + item.variantId + '">' +
      '<div class="bmb-cart-item__img">' + (image ? '<img src="' + image + '" alt="' + title + '" width="48" height="48" loading="lazy">' : '') + '</div>' +
      '<div class="bmb-cart-item__info">' +
        '<p class="bmb-cart-item__name">' + title + '</p>' +
        '<p class="bmb-cart-item__price">' + fmt(unit * item.qty) + '</p>' +
      '</div>' +
      '<div class="bmb-cart-item__right">' +
        '<button class="bmb-cart-item__remove" data-action="remove-extra" data-variant-id="' + item.variantId + '" aria-label="' + S.remove + '">✕</button>' +
        '<div class="bmb-qty">' +
          '<button class="bmb-qty__btn bmb-qty__btn--minus" data-action="extra-minus" data-variant-id="' + item.variantId + '">−</button>' +
          '<span class="bmb-qty__val">' + item.qty + '</span>' +
          '<button class="bmb-qty__btn bmb-qty__btn--plus" data-action="extra-plus" data-variant-id="' + item.variantId + '">+</button>' +
        '</div>' +
      '</div>' +
    '</div>';
  }

  function buildExtrasCartSection() {
    var extras = STATE.extrasItems.filter(function (i) { return !i.isGift; });
    if (extras.length === 0) return '';
    var html = [];
    html.push(
      '<div class="bmb-cart-section-label bmb-cart-section-label--row">' +
        '<span>' + S.extras + '</span>' +
      '</div>'
    );
    extras.forEach(function (item) {
      html.push(buildExtrasCartItem(item));
      if (isBogoExtra(item)) html.push(buildBogoGiftRow(item));
    });
    return html.join('');
  }

  // Footer (round 16): one number (plan price + pan), no one-time
  // strike-through compare. Free-shipping pill + postal link stay from
  // round 15. Renewal note always shows (subscription-only page).
  function buildFooterHtml(idPrefix) {
    var total = totalCents();
    var cnt = itemCount();
    // Full-box gate: item count === tier capacity, same requirement
    // the live builder enforces (bab-custom.js's
    // bundleCount() === STATE.selectedSize), not a dollar minimum.
    // Builder/drawer never Checkout; extras panel owns that CTA.
    var full = boxIsFull();
    var canContinue = full && cnt > 0;
    var remaining = Math.max(0, tierCapacity() - cnt);

    return (
      '<div class="bmb-footer-compact">' +
        '<div class="bmb-footer-compact__row bmb-footer-compact__row--summary">' +
          '<span class="bmb-footer-compact__sub-label">' + S.subtotal + '</span>' +
          '<div class="bmb-footer-compact__subtotal">' +
            '<span class="bmb-footer-compact__sub-amount">' + fmt(total) + '</span>' +
          '</div>' +
        '</div>' +
        buildFooterShippingHtml() +
        (canContinue
          ? '<button type="button" class="bmb-cta-btn" id="' + idPrefix + '-continue-btn" data-action="open-extras">' + continueCtaLabel() + '</button>'
          : '<button type="button" class="bmb-cta-btn" disabled>' + (remaining === 1 ? S.addMoreItems.replace('{n}', remaining) : S.addMoreItemsPlural.replace('{n}', remaining)) + '</button>') +
        buildRenewalNote() +
      '</div>'
    );
  }

  // Sep 2 2026: muted "Shipping fee applies" pill is the shipcalc
  // trigger (no separate "Check shipping to your postal code" link).
  // Green "Free shipping" pill stays a non-link. When the selected
  // tier is below 10, a green upgrade pill sits under the fee pill
  // (not overlapping Checkout) and selectTier(10)s in place.
  function buildFooterShippingHtml() {
    var shipReached = tierQualifiesFreeShipping();
    var pillInner =
      '<span class="bmb-footer-ship__icon" aria-hidden="true">' + (shipReached ? '✓' : '🚚') + '</span>' +
      '<span class="bmb-footer-ship__label">' + (shipReached ? S.shipNode : S.shippingFeeApplies) + '</span>';
    var pill = shipReached
      ? '<span class="bmb-footer-ship__pill">' + pillInner + '</span>'
      : '<button type="button" class="bmb-footer-ship__pill bmb-footer-ship__pill--link" data-action="open-shipcalc">' + pillInner + '</button>';
    var upgrade = (!shipReached && STATE.selectedTier < 10)
      ? '<button type="button" class="bmb-footer-ship__upgrade" data-action="footer-upgrade-10">' + (S.footerUpgrade10 || 'Upgrade to 10 for free shipping') + '</button>'
      : '';
    return (
      '<div class="bmb-footer-ship' + (shipReached ? ' bmb-footer-ship--reached' : ' bmb-footer-ship--muted') + '">' +
        pill +
        upgrade +
      '</div>'
    );
  }

  // Renewal disclosure under the CTA. Always shown: this page is
  // subscription only (round 16). Middle-dot separator, never a dash.
  function buildRenewalNote() {
    if (BMB_GIFTING || isOneTimeSelected()) return '';
    return '<p class="bmb-renewal-note">' + S.renewsAuto + ' \u00b7 ' + S.cancelAnytime + ', ' + S.noFee + '</p>';
  }

  function buildGiftNoteRow() {
    if (!BMB_GIFTING) return '';
    var note = String(STATE.giftNote || '').trim();
    if (!note) return '';
    return '<div class="bmb-cart-item bmb-cart-item--gift-note">' +
      '<div class="bmb-cart-item__info">' +
        '<p class="bmb-cart-item__name">' + S.giftNote + '</p>' +
        '<p class="bmb-cart-item__price">' + fmt(0) + '</p>' +
        '<p class="bmb-cart-item__note-text">' + escapeHtml(note) + '</p>' +
      '</div>' +
      '<div class="bmb-cart-item__right">' +
        '<button class="bmb-cart-item__remove" data-action="remove-gift-note" aria-label="' + S.giftNoteRemove + '">✕</button>' +
      '</div>' +
    '</div>';
  }

  /* --------------------------------------------------------
     6. DRAWER RENDER
  -------------------------------------------------------- */
  function renderDrawer() {
    if (!el.drawerBody) return;
    var freshBody = el.drawerBody.cloneNode(false);
    freshBody.innerHTML = buildCartBody();
    el.drawerBody.replaceWith(freshBody);
    el.drawerBody = freshBody;
    attachItemRowEvents(el.drawerBody);
    renderDrawerFooter();
  }

  function renderDrawerFooter() {
    if (!el.drawerFooter) return;
    var freshFooter = el.drawerFooter.cloneNode(false);
    freshFooter.innerHTML = buildFooterHtml('bmb-drawer');
    el.drawerFooter.replaceWith(freshFooter);
    el.drawerFooter = freshFooter;
    var continueBtn = $('bmb-drawer-continue-btn');
    if (continueBtn) continueBtn.addEventListener('click', openExtrasPanel);
  }

  function attachItemRowEvents(container) {
    if (!container) return;
    container.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-action]');
      if (btn) {
        var action = btn.dataset.action;
        var vid = parseInt(btn.dataset.variantId, 10);
        if (action === 'remove-item') removeItem(vid);
        else if (action === 'item-minus') changeItemQty(vid, -1);
        else if (action === 'item-plus') changeItemQty(vid, +1);
        else if (action === 'remove-extra') { removeExtra(vid); }
        else if (action === 'extra-minus') changeExtraQty(vid, -1);
        else if (action === 'extra-plus') changeExtraQty(vid, +1);
        else if (action === 'open-extras') { openExtrasPanel(); }
        else if (action === 'pan-prompt-yes') {
          STATE.panJustAdded = true;
          setPan(false);
        }
        else if (action === 'pan-prompt-no') { removePan(); }
        else if (action === 'fill-slot') {
          // Empty-slot row tap: take the shopper back to the fill grid
          // (closing the overlay drawer first when one is open).
          if (STATE.activePanel) closeAllPanels();
          scrollToFillGrid();
        }
        else if (action === 'remove-gift-note') { STATE.giftNote = ''; render(); }
        else if (action === 'toggle-pan-info') {
          var panel = container.querySelector('.bmb-pan-info-panel');
          if (panel) panel.style.display = panel.style.display === 'none' ? '' : 'none';
        } else if (action === 'clear-box') {
          handleClearBoxClick();
        }
        return;
      }

      // Pan block actions (buildPanBlock): id-based, copied from bab-custom.js.
      if (e.target.closest('#bmb-pan-upgrade')) { setPan(true); return; }
      if (e.target.closest('#bmb-pan-downgrade')) { setPan(false); return; }
      if (e.target.closest('#bmb-pan-remove')) { openPanWarning(); return; }
      if (e.target.closest('#bmb-pan-readd')) { setPan(false); return; }
    });
  }

  /* --------------------------------------------------------
     7. DESKTOP SIDEBAR RENDER
  -------------------------------------------------------- */
  function renderDesktopSidebar() {
    if (!el.sidebarBody) return;
    var freshBody = el.sidebarBody.cloneNode(false);
    freshBody.innerHTML = buildCartBody();
    el.sidebarBody.replaceWith(freshBody);
    el.sidebarBody = freshBody;
    attachItemRowEvents(el.sidebarBody);

    if (el.sidebarFooter) {
      var freshFooter = el.sidebarFooter.cloneNode(false);
      freshFooter.innerHTML = buildFooterHtml('bmb-sidebar');
      el.sidebarFooter.replaceWith(freshFooter);
      el.sidebarFooter = freshFooter;
      var continueBtn = $('bmb-sidebar-continue-btn');
      if (continueBtn) continueBtn.addEventListener('click', openExtrasPanel);
    }
  }

  /* --------------------------------------------------------
     8. ITEM LOGIC (round 15, Aug 18 2026): 1 item qty = 1 box slot,
     exactly live's bab-custom.js addBundleItem/changeBundleQty. Only
     ever blocked at the absolute largest configured tier (absMax);
     crossing a smaller tier's own size auto-upsizes via autoSelectSize,
     never blocks.
  -------------------------------------------------------- */
  function addItem(variantId, productId, collectionId, title, titleEn, image) {
    var absMax = absoluteMaxSize();
    if (itemCount() >= absMax) return;
    var liveCol = resolveLiveCollectionId(variantId, collectionId);
    var existing = STATE.items.find(function (i) { return i.variantId === variantId && !i.isGift; });
    if (existing) {
      existing.qty++;
      if (liveCol) existing.collectionId = liveCol;
    } else {
      STATE.items.push({ variantId: variantId, productId: productId, collectionId: liveCol || collectionId, title: title, titleEn: titleEn || title, image: image, qty: 1 });
    }
    autoSelectSize(itemCount());
    syncPanAutoInclude();
    render();
  }

  function applyHubSeed() {
    var raw = null;
    try {
      raw = sessionStorage.getItem('bmb_seed');
      sessionStorage.removeItem('bmb_seed');
    } catch (e) { return; }
    if (!raw) return;
    var seed;
    try { seed = JSON.parse(raw); } catch (err) { return; }
    if (!seed) return;
    var variantId = parseInt(seed.variantId, 10);
    var productId = parseInt(seed.productId, 10);
    var qty = parseInt(seed.qty, 10);
    if (!qty || qty < 1) qty = 1;
    var catalog = null;
    if (variantId) catalog = ITEMS.find(function (i) { return i.variantId === variantId; });
    if (!catalog && productId) catalog = ITEMS.find(function (i) { return i.productId === productId; });
    if (!catalog) return;
    if (catalog.available === false || catalog.comingSoon) return;
    if (typeof BMB_GIFTING !== 'undefined' && BMB_GIFTING) return;
    var n;
    for (n = 0; n < qty; n++) {
      if (itemCount() >= absoluteMaxSize()) break;
      addItem(catalog.variantId, catalog.productId, catalog.collectionId, catalog.title, catalog.title, catalog.image);
    }
  }

  function changeItemQty(variantId, delta) {
    var item = STATE.items.find(function (i) { return i.variantId === variantId && !i.isGift; });
    if (!item) return;
    if (delta > 0) {
      var absMax = absoluteMaxSize();
      if (itemCount() >= absMax) return;
    }
    item.qty = Math.max(0, item.qty + delta);
    if (item.qty === 0) {
      STATE.items = STATE.items.filter(function (i) { return i.variantId !== variantId || i.isGift; });
    }
    autoSelectSize(itemCount());
    syncPanAutoInclude();
    render();
  }

  function removeItem(variantId) {
    STATE.items = STATE.items.filter(function (i) { return i.variantId !== variantId || i.isGift; });
    syncPanAutoInclude();
    render();
  }

  /* --------------------------------------------------------
     8z. TIER SELECTION (round 15, Aug 18 2026): mirrors live's own
     selectSize() exactly. Picking a smaller tier than the box's current
     item count trims from the END of the item list (most-recently-added
     first) to fit, never blocks the switch with an alert and never
     drops the whole box. Picking a bigger tier always just succeeds.
  -------------------------------------------------------- */
  function selectTier(size) {
    size = parseInt(size, 10);
    if (size === STATE.selectedTier) return;
    var tier = BOX_SIZES.find(function (b) { return b.size === size; });
    if (!tier) return;
    STATE.selectedTier = size;
    var removed = itemCount() - size;
    if (removed > 0) {
      var reversed = STATE.items.filter(function (i) { return !i.isGift; }).slice().reverse();
      for (var k = 0; k < reversed.length && removed > 0; k++) {
        var reduce = Math.min(reversed[k].qty, removed);
        reversed[k].qty -= reduce;
        removed -= reduce;
      }
      STATE.items = STATE.items.filter(function (i) { return i.isGift || i.qty > 0; });
    }
    syncPanAutoInclude();
    render();
  }

  /* --------------------------------------------------------
     8a. CLEAR BOX (round 10, Aug 16 2026, item 6): discreet link-style
     control next to "Your selections" in the cart body (sidebar + drawer,
     buildCartBody()), gated by a lightweight two-tap confirm instead of a
     full modal (click once -> label flips to "Sure? Clear" for 3s, click
     again within that window -> actually clears). Resets pan state to
     fully neutral (panUserIntent: null), not "removed", so a fresh add
     afterwards still auto-includes the pan normally: this is meant to be
     a clean-slate reset, not a standing "no pan" preference.
  -------------------------------------------------------- */
  function clearBox() {
    STATE.items = STATE.items.filter(function (i) { return i.isGift; });
    STATE.panVariantId = null;
    STATE.isPanCastIron = false;
    STATE.panUserIntent = null;
    STATE.panJustAdded = false;
    STATE._clearBoxPending = false;
    try { sessionStorage.removeItem('bmb_atc_fired'); sessionStorage.removeItem('bmb_cart_sig'); } catch (e) {}
    render();
  }

  function handleClearBoxClick() {
    if (STATE._clearBoxPending) {
      clearTimeout(clearBoxConfirmTimer);
      clearBox();
      return;
    }
    STATE._clearBoxPending = true;
    clearTimeout(clearBoxConfirmTimer);
    clearBoxConfirmTimer = setTimeout(function () {
      STATE._clearBoxPending = false;
      render();
    }, 3000);
    render();
  }

  /* --------------------------------------------------------
     8b. DISCOVERY BOX: "Fill the perfect discovery box" (round 17, Aug
     23 2026): fills the currently selected tier (6 / 10 / 12 credits),
     never forcing Medium. Composition rules unchanged from round 15
     (best sellers first, then guaranteed category variety, then a
     round-robin top-up pass). Every pick costs exactly 1 slot; cap is
     tierCapacity(), not a fixed 10. Deterministic: every pass walks ITEMS/BEST_SELLERS/category
     lists in their fixed catalog order, no randomness. Skips
     OOS/coming-soon. Pan auto-add (syncPanAutoInclude) still runs
     after, unchanged. Does not write BYB products or Recharge.
  -------------------------------------------------------- */
  // Role keys (bread/pastry/gourmet/fresh-pasta/vegan), not collection
  // handles: emitted per item by sections/bmb-builder.liquid from the
  // section's col_1..col_6 settings, so this stays correct even if the
  // underlying collection handles are swapped again (Oct 6 2026 fix:
  // the Sep 21 BYB collection switch left these matching the old
  // bmb-* handles, so passes 2/3 matched nothing and the fill stopped
  // at the 6 best sellers).
  var BMB_DISCOVERY_CATEGORY_ORDER = ['bread', 'pastry', 'gourmet', 'fresh-pasta', 'vegan'];
  // Variety slots: one bread, one pastry, one gourmet. Extras are no
  // longer fill-grid items (round 18), so they are not a variety slot
  // and cannot be pulled in by top-up.
  var BMB_DISCOVERY_VARIETY_SLOTS = ['bread', 'pastry', 'gourmet'];

  function bmbDiscoveryCategoryOf(entry) {
    return entry.role || entry.collectionHandle || entry.category || '';
  }

  function fillDiscoveryBox() {
    var capacity = tierCapacity();
    if (capacity <= 0) return;
    var seenVariant = {};
    var seenProduct = {};
    var seenCategory = {};
    var picks = [];
    var remaining = capacity;

    // Gifts are preserved alongside picks (gifts.concat(picks) below), so
    // a gift's product/variant must not be re-picked as a "duplicate" SKU.
    STATE.items.forEach(function (i) {
      if (!i.isGift) return;
      if (i.variantId != null) seenVariant[i.variantId] = true;
      if (i.productId != null) seenProduct[i.productId] = true;
    });

    function add(entry) {
      if (!entry || entry.variantId == null) return false;
      if (seenVariant[entry.variantId]) return false;
      if (entry.productId != null && seenProduct[entry.productId]) return false;
      if (entry.available === false || entry.comingSoon) return false;
      if (remaining <= 0) return false; // every item costs exactly 1 slot, never exceed the tier's capacity
      seenVariant[entry.variantId] = true;
      if (entry.productId != null) seenProduct[entry.productId] = true;
      seenCategory[bmbDiscoveryCategoryOf(entry)] = true;
      var card = document.querySelector('.bmb-product-card[data-variant-id="' + entry.variantId + '"]');
      var nameEl = card && card.querySelector('.bmb-product-card__name');
      picks.push({
        variantId: entry.variantId,
        productId: entry.productId,
        collectionId: resolveLiveCollectionId(entry.variantId, entry.collectionId) || entry.collectionId,
        title: (nameEl && nameEl.textContent.trim()) || entry.title,
        titleEn: (card && card.dataset.title) || entry.title,
        image: (card && card.dataset.image) || entry.image,
        qty: 1
      });
      remaining -= 1;
      return true;
    }

    // 1. Best sellers, the mandatory starting base, slots permitting.
    BEST_SELLERS.forEach(function (bs) { add(bs); });

    // 2. Variety pass: fills each mandatory slot (bread / pastry /
    // gourmet) not already covered by the best sellers, slots
    // permitting -- a small box may not have room for every category,
    // in which case this pass simply does what it can.
    BMB_DISCOVERY_VARIETY_SLOTS.forEach(function (slot) {
      var handles = Array.isArray(slot) ? slot : [slot];
      if (handles.some(function (h) { return seenCategory[h]; })) return;
      for (var h = 0; h < handles.length; h++) {
        var handle = handles[h];
        var addedOne = false;
        for (var i = 0; i < ITEMS.length; i++) {
          var it = ITEMS[i];
          if (bmbDiscoveryCategoryOf(it) !== handle) continue;
          if (add(it)) { addedOne = true; break; }
        }
        if (addedOne) break;
      }
    });

    // 3. Top-up pass: round-robins the full category list, adding the
    // first item per category per lap that still fits in `remaining`
    // slots, until the tier is exactly full or nothing left fits.
    // Bounded loop as a safety net; in practice it exits via the
    // "nothing added this lap" break well before 200 laps.
    var guard = 0;
    while (remaining > 0 && guard++ < 200) {
      var addedThisLap = false;
      for (var c = 0; c < BMB_DISCOVERY_CATEGORY_ORDER.length; c++) {
        if (remaining <= 0) break;
        var handle2 = BMB_DISCOVERY_CATEGORY_ORDER[c];
        for (var j = 0; j < ITEMS.length; j++) {
          var it2 = ITEMS[j];
          if (bmbDiscoveryCategoryOf(it2) !== handle2) continue;
          if (add(it2)) { addedThisLap = true; break; }
        }
      }
      if (!addedThisLap) break; // nothing left fits in the remaining slots
    }

    if (!picks.length) return;

    var gifts = STATE.items.filter(function (i) { return i.isGift; });
    STATE.items = gifts.concat(picks);
    syncPanAutoInclude();
    render();

    // Meta: trackCustom (not standard track) so FillDiscoveryBox stays a
    // builder-only signal and does not compete with Shopify channel AddToCart
    // on real /cart/add. Scratch theme #154604142775 has zero theme-side fbq;
    // Sep 15 stock comparison: no theme ATC on drawer open (was wrongly firing
    // AddToCart from openDrawer).
    bmbFireMetaCustom('FillDiscoveryBox', {
      content_ids: picks.map(function (p) { return String(p.variantId); }),
      content_type: 'product',
      content_name: 'Discovery Box',
      content_category: 'BMB',
      value: totalCents() / 100,
      currency: 'CAD',
      num_items: picks.length
    });

    // Mobile-only (round 9, Aug 16 2026): on desktop the always-visible
    // inline sidebar already shows the fill result, so popping the
    // overlay drawer on top of it is redundant/wrong. "Mobile" here is
    // the exact breakpoint at which bmb-builder.css hides the inline
    // sidebar (960px, see #bmb-app's grid-template-columns rule), not
    // the old 768px guess.
    if (window.innerWidth < 960) {
      setTimeout(openDrawer, 350);
    }
  }

  /* --------------------------------------------------------
     9. PANEL CONTROL
  -------------------------------------------------------- */
  function openDrawer() {
    STATE.activePanel = 'drawer';
    setScrollLock('panel', true);
    if (el.backdrop) el.backdrop.classList.add('bmb-backdrop--visible');
    if (el.drawer) el.drawer.classList.add('bmb-drawer--open');
    if (el.drawer2) el.drawer2.classList.remove('bmb-drawer--open');
    renderDrawer();
  }
  function openExtrasPanel() {
    if (panChoiceOutstanding()) {
      showPanChoiceGate();
      return;
    }
    STATE.activePanel = 'extras';
    setScrollLock('panel', true);
    if (el.backdrop) el.backdrop.classList.add('bmb-backdrop--visible');
    if (window.innerWidth < 960 && el.drawer) el.drawer.classList.add('bmb-drawer--open');
    if (el.drawer2) el.drawer2.classList.add('bmb-drawer--open');
    if (el.drawer && el.drawer.classList.contains('bmb-drawer--open')) renderDrawer();
    renderExtrasDrawer();
    commitCartFromState({ reason: 'extras' }).catch(function () {});
  }
  function closeExtrasPanel() {
    STATE.extrasDismissed = true;
    if (el.drawer2) el.drawer2.classList.remove('bmb-drawer--open');
    if (el.drawer && el.drawer.classList.contains('bmb-drawer--open')) {
      STATE.activePanel = 'drawer';
      renderDrawer();
      renderDesktopSidebar();
      syncToGlobalBar();
      return;
    }
    closeAllPanels();
  }
  function closeAllPanels() {
    STATE.activePanel = null;
    setScrollLock('panel', false);
    if (el.backdrop) el.backdrop.classList.remove('bmb-backdrop--visible');
    if (el.drawer) el.drawer.classList.remove('bmb-drawer--open');
    if (el.drawer2) el.drawer2.classList.remove('bmb-drawer--open');
    render();
  }

  function changeExtraQty(variantId, delta) {
    var extra = EXTRAS.find(function (e) { return e.variantId === variantId; });
    var deal = bogoProduct(variantId);
    if (!extra && deal) extra = deal;
    var existing = STATE.extrasItems.find(function (i) { return i.variantId === variantId; });
    if (delta > 0) {
      if (!extra || extra.available === false) return;
      if (deal && (existing ? existing.qty : 0) >= BOGO_MAX_PAID) return;
      if (existing) existing.qty++;
      else STATE.extrasItems.push({
        variantId: variantId,
        productId: extra.productId,
        title: extra.title,
        image: extra.image,
        price: extra.price,
        qty: 1
      });
    } else if (existing) {
      existing.qty = Math.max(0, existing.qty - 1);
      if (existing.qty === 0) STATE.extrasItems = STATE.extrasItems.filter(function (i) { return i.variantId !== variantId; });
    }
    render();
  }
  function removeExtra(variantId) {
    STATE.extrasItems = STATE.extrasItems.filter(function (i) { return i.variantId !== variantId; });
    render();
  }

  function buildAddonDealCard(product) {
    var qty = 0;
    var existing = STATE.extrasItems.find(function (i) { return i.variantId === product.variantId; });
    if (existing) qty = existing.qty;
    var name = (product.title || '').replace(/"/g, '&quot;');
    var img = product.image || '';
    var pill = BOGO.pill || 'LIMITED DEAL';
    var atCap = qty >= BOGO_MAX_PAID;
    var action = product.available === false
      ? '<button class="bmb-atb-btn bmb-atb-btn--oos" type="button" disabled>' + S.outOfStock + '</button>'
      : (qty === 0
          ? '<button class="bmb-atb-btn" type="button" data-action="add-extra" data-variant-id="' + product.variantId + '">' + S.addBtn + '</button>'
          : '<div class="bmb-qty" style="width:96px;" role="group" aria-label="' + S.quantity + '">' +
              '<button class="bmb-qty__btn bmb-qty__btn--minus" type="button" data-action="extra-minus" data-variant-id="' + product.variantId + '" aria-label="' + S.removeOne + '">−</button>' +
              '<span class="bmb-qty__val" aria-live="polite">' + qty + '</span>' +
              '<button class="bmb-qty__btn bmb-qty__btn--plus" type="button" data-action="extra-plus" data-variant-id="' + product.variantId + '" aria-label="' + S.addOne + '"' + (atCap ? ' disabled' : '') + '>+</button>' +
            '</div>');
    return (
      '<div class="bmb-extra-card bmb-extra-card--bogo" data-variant-id="' + product.variantId + '">' +
        '<div class="bmb-extra-card__img">' +
          '<span class="bmb-extra-card__deal-banner">' + pill + '</span>' +
          (img ? '<img src="' + img + '" alt="' + name + '" width="400" height="400" loading="lazy">' : '') +
        '</div>' +
        '<div class="bmb-extra-card__body">' +
          '<p class="bmb-extra-card__name">' + product.title + '</p>' +
          '<p class="bmb-extra-card__price">' + fmt(product.price) + ' <span class="bmb-addon-deal__plus-free">+ ' + S.free + '</span></p>' +
          action +
        '</div>' +
      '</div>'
    );
  }

  function buildAddonDealHtml() {
    if (!bogoActive()) return '';
    var heading = S.bogoDealTitle || BOGO.heading || 'Limited add-on deal';
    var copy = S.bogoDealCopy || BOGO.subheading || '';
    var pill = BOGO.pill || 'LIMITED DEAL';
    var timerLabel = S.bogoTimerLabel || BOGO.timerLabel || 'Ends in';
    var cards = BOGO_PRODUCTS.map(buildAddonDealCard).join('');
    return (
      '<div class="bmb-addon-deal">' +
        '<div class="bmb-addon-deal__banner">' + pill + '</div>' +
        '<div class="bmb-addon-deal__inner">' +
          '<div class="bmb-addon-deal__head">' +
            '<p class="bmb-addon-deal__title">' + heading + '</p>' +
            (BOGO_ENDS_AT
              ? '<span class="bmb-addon-deal__timer" role="timer">' +
                  '<span class="bmb-addon-deal__timer-label">' + timerLabel + '</span>' +
                  '<span class="bmb-addon-deal__timer-value" data-bmb-bogo-countdown>' + bogoCountdownText() + '</span>' +
                '</span>'
              : '') +
          '</div>' +
          (copy ? '<p class="bmb-addon-deal__copy">' + copy + '</p>' : '') +
        '</div>' +
      '</div>' +
      '<div class="bmb-extras-grid bmb-addon-deal__grid" role="group" aria-label="' + heading + '">' + cards + '</div>'
    );
  }

  function buildExtraCard(extra, qty) {
    var name = (extra.title || '').replace(/"/g, '&quot;');
    var desc = (extra.description || '').replace(/"/g, '&quot;');
    var img = extra.image || '';
    var priceHtml = (extra.comparePrice ? '<s>' + fmt(extra.comparePrice) + '</s> ' : '') + fmt(extra.price);
    var action = extra.available === false
      ? '<button class="bmb-atb-btn bmb-atb-btn--oos" type="button" disabled>' + S.outOfStock + '</button>'
      : (qty === 0
          ? '<button class="bmb-atb-btn" type="button" data-action="add-extra" data-variant-id="' + extra.variantId + '">' + S.addBtn + '</button>'
          : '<div class="bmb-qty" style="width:96px;" role="group" aria-label="' + S.quantity + '">' +
              '<button class="bmb-qty__btn bmb-qty__btn--minus" type="button" data-action="extra-minus" data-variant-id="' + extra.variantId + '" aria-label="' + S.removeOne + '">−</button>' +
              '<span class="bmb-qty__val" aria-live="polite">' + qty + '</span>' +
              '<button class="bmb-qty__btn bmb-qty__btn--plus" type="button" data-action="extra-plus" data-variant-id="' + extra.variantId + '" aria-label="' + S.addOne + '">+</button>' +
            '</div>');
    return (
      '<div class="bmb-extra-card" data-variant-id="' + extra.variantId + '">' +
        '<div class="bmb-extra-card__img" data-action="open-extra-modal" data-title="' + name + '" data-image="' + img + '" data-description="' + desc + '">' +
          (img ? '<img src="' + img + '" alt="' + name + '" width="400" height="400" loading="lazy">' : '') +
        '</div>' +
        '<div class="bmb-extra-card__body">' +
          '<p class="bmb-extra-card__name">' + extra.title + '</p>' +
          '<p class="bmb-extra-card__price">' + priceHtml + '</p>' +
          action +
        '</div>' +
      '</div>'
    );
  }

  function renderExtrasDrawer() {
    if (!el.drawer2Body) return;
    var titleEl = $('bmb-d2-title');
    if (titleEl) titleEl.textContent = S.addExtrasTitle;
    var q = (STATE.extrasSearch || '').trim().toLowerCase();
    var visible = EXTRAS.filter(function (e) {
      if (BOGO_VARIANT_IDS.has(e.variantId)) return false;
      return !q || (e.title || '').toLowerCase().indexOf(q) !== -1;
    });
    var html = [];
    if (bogoActive()) html.push(buildAddonDealHtml());
    html.push(
      '<div class="bmb-search-wrap bmb-extras-search">' +
        '<input class="bmb-search-input" id="bmb-extras-search" type="search" placeholder="' + S.searchExtras + '" aria-label="' + S.searchExtrasLabel + '" value="' + String(STATE.extrasSearch || '').replace(/"/g, '&quot;') + '">' +
      '</div>'
    );
    if (EXTRAS.length === 0) {
      html.push('<div class="bmb-empty"><div class="bmb-empty__icon">🛍️</div><div class="bmb-empty__text">' + S.noExtrasAvailable + '</div></div>');
    } else if (visible.length === 0) {
      html.push('<div class="bmb-empty"><div class="bmb-empty__icon">🔍</div><div class="bmb-empty__text">' + S.noExtrasMatch + '</div></div>');
    } else {
      html.push('<div class="bmb-extras-grid" role="group" aria-label="' + S.addExtrasTitle + '">');
      visible.forEach(function (extra) {
        var stateItem = STATE.extrasItems.find(function (i) { return i.variantId === extra.variantId; });
        html.push(buildExtraCard(extra, stateItem ? stateItem.qty : 0));
      });
      html.push('</div>');
    }
    var fresh = el.drawer2Body.cloneNode(false);
    fresh.innerHTML = html.join('');
    el.drawer2Body.replaceWith(fresh);
    el.drawer2Body = fresh;
    fresh.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-action]');
      if (!btn) return;
      var action = btn.dataset.action;
      var vid = parseInt(btn.dataset.variantId, 10);
      if (action === 'add-extra' || action === 'extra-plus') changeExtraQty(vid, +1);
      else if (action === 'extra-minus') changeExtraQty(vid, -1);
      else if (action === 'open-extra-modal') {
        openProductModal({ title: btn.dataset.title, image: btn.dataset.image, description: btn.dataset.description });
      }
    });
    var searchInput = $('bmb-extras-search');
    if (searchInput) {
      if (STATE.extrasSearch) {
        searchInput.focus();
        searchInput.setSelectionRange(searchInput.value.length, searchInput.value.length);
      }
      searchInput.addEventListener('input', function () {
        STATE.extrasSearch = searchInput.value;
        renderExtrasDrawer();
      });
    }
    renderExtrasFooter();
  }

  function renderExtrasFooter() {
    if (!el.drawer2Footer) return;
    var total = totalCents();
    var extrasTotal = extrasTotalCents();
    var fresh = el.drawer2Footer.cloneNode(false);
    fresh.innerHTML =
      '<div class="bmb-footer-compact">' +
        '<div class="bmb-footer-compact__row bmb-footer-compact__row--summary">' +
          '<span class="bmb-footer-compact__sub-label">' + S.subtotal + '</span>' +
          '<div class="bmb-footer-compact__subtotal">' +
            (extrasTotal > 0 ? '<span class="bmb-footer-compact__extras-note">' + S.extras + ' ' + fmt(extrasTotal) + '</span>' : '') +
            '<span class="bmb-footer-compact__sub-amount">' + fmt(total) + '</span>' +
          '</div>' +
        '</div>' +
        '<button type="button" class="bmb-cta-btn" id="bmb-extras-checkout-btn">' + S.checkout + ' \u00b7 ' + fmt(total) + '</button>' +
        buildRenewalNote() +
      '</div>';
    el.drawer2Footer.replaceWith(fresh);
    el.drawer2Footer = fresh;
    var checkoutBtn = $('bmb-extras-checkout-btn');
    if (checkoutBtn) checkoutBtn.addEventListener('click', function (e) {
      if (e && e.preventDefault) e.preventDefault();
      STATE.extrasDismissed = true;
      handleCheckout();
    });
  }

  /* --------------------------------------------------------
     10. PRODUCT MODAL
  -------------------------------------------------------- */
  function openProductModal(data) {
    if (!el.productModal) return;
    var titleEl = el.productModal.querySelector('.bmb-modal__title');
    var imgEl = el.productModal.querySelector('.bmb-modal__img');
    var bodyEl = el.productModal.querySelector('.bmb-modal__body');
    if (titleEl) titleEl.textContent = data.title || '';
    if (imgEl) {
      if (data.image) { imgEl.src = data.image; imgEl.alt = data.title || ''; imgEl.style.display = ''; }
      else { imgEl.removeAttribute('src'); imgEl.alt = ''; imgEl.style.display = 'none'; }
    }
    if (bodyEl) {
      var html = '';
      if (data.description) html += '<p class="bmb-modal__text">' + data.description + '</p>';
      if (data.ingredients) html += '<p class="bmb-modal__section-title">' + S.ingredients + '</p><div class="bmb-modal__richtext">' + data.ingredients + '</div>';
      if (data.allergens) html += '<p class="bmb-modal__section-title">' + S.allergens + '</p><div class="bmb-modal__richtext">' + data.allergens + '</div>';
      if (data.instructions) html += '<p class="bmb-modal__section-title">' + S.bakingInstructions + '</p><div class="bmb-modal__richtext">' + data.instructions + '</div>';
      if (data.nutriImg) html += '<p class="bmb-modal__section-title">' + S.nutritionFacts + '</p><img class="bmb-modal__nutri-img" src="' + data.nutriImg + '" alt="' + S.nutritionFacts + '" width="600" height="600" loading="lazy">';
      bodyEl.innerHTML = html || '<p class="bmb-modal__text">' + S.noAdditionalInfo + '</p>';
    }
    el.productModal.classList.add('bmb-modal-overlay--open');
    setScrollLock('modal', true);
  }
  function closeProductModal() {
    if (!el.productModal) return;
    el.productModal.classList.remove('bmb-modal-overlay--open');
    setScrollLock('modal', false);
  }

  function openGiftNoteModal() {
    if (!BMB_GIFTING || !el.giftNoteModal) return;
    var input = document.getElementById('bmb-gift-note-input');
    if (input) input.value = STATE.giftNote || '';
    el.giftNoteModal.classList.add('bmb-modal-overlay--open');
    setScrollLock('giftnote', true);
    if (input) setTimeout(function () { input.focus(); }, 40);
  }
  function closeGiftNoteModal() {
    if (!el.giftNoteModal) return;
    el.giftNoteModal.classList.remove('bmb-modal-overlay--open');
    setScrollLock('giftnote', false);
  }
  function saveGiftNoteFromModal() {
    var input = document.getElementById('bmb-gift-note-input');
    var next = input ? String(input.value || '').trim() : '';
    if (next.length > 250) next = next.slice(0, 250);
    STATE.giftNote = next;
    closeGiftNoteModal();
    render();
  }

  /* --------------------------------------------------------
     PAN WARNING MODAL (copied from bab-custom.js)
  -------------------------------------------------------- */
  function openPanWarning() {
    if (!el.panWarning) return;
    el.panWarning.classList.add('bmb-modal-overlay--open');
    setScrollLock('panwarn', true);
  }
  function closePanWarning() {
    if (!el.panWarning) return;
    el.panWarning.classList.remove('bmb-modal-overlay--open');
    setScrollLock('panwarn', false);
  }

  /* --------------------------------------------------------
     11. FILTER + SEARCH
  -------------------------------------------------------- */
  function insertCategoryHeaders() {
    var grid = document.getElementById('bmb-grid');
    if (!grid) return;
    var filterBtns = [].slice.call(document.querySelectorAll('.bmb-filter-btn[data-filter]:not([data-filter="all"])'));
    filterBtns.forEach(function (btn) {
      var handle = btn.dataset.filter;
      var label = btn.textContent.trim();
      var firstCard = grid.querySelector('.bmb-product-card[data-category="' + handle + '"]');
      if (!firstCard) return;
      var header = document.createElement('div');
      header.className = 'bmb-category-header bmb-hidden';
      header.dataset.forCategory = handle;
      header.textContent = label;
      grid.insertBefore(header, firstCard);
    });
  }

  function applyFilter(filter) {
    STATE.activeFilter = filter;
    var q = STATE.searchQuery.trim().toLowerCase();
    document.querySelectorAll('.bmb-filter-btn').forEach(function (btn) {
      btn.classList.toggle('bmb-filter-btn--active', btn.dataset.filter === filter);
    });
    document.querySelectorAll('.bmb-category-header').forEach(function (h) {
      h.classList.toggle('bmb-hidden', filter !== 'all');
    });
    document.querySelectorAll('.bmb-product-card').forEach(function (card) {
      // Best Sellers golden row stays visible through every category filter
      // (round 6, Aug 16 2026), same guard as bab-custom.js's applyFilter().
      if (card.dataset.bestseller === 'true') { card.classList.remove('bmb-hidden'); return; }
      var cat = card.dataset.category || '';
      var title = (card.dataset.title || '').toLowerCase();
      var matchesFilter = filter === 'all' || cat === filter;
      var matchesSearch = !q || title.includes(q);
      card.classList.toggle('bmb-hidden', !(matchesFilter && matchesSearch));
    });
  }

  function filterBySearch(query) {
    STATE.searchQuery = query;
    var q = query.trim().toLowerCase();
    document.querySelectorAll('.bmb-product-card').forEach(function (card) {
      // Best Sellers golden row stays visible through search too (round 6).
      if (card.dataset.bestseller === 'true') { card.classList.remove('bmb-hidden'); return; }
      var title = (card.dataset.title || '').toLowerCase();
      var cat = card.dataset.category || 'all';
      var matchesSearch = !q || title.includes(q);
      var matchesFilter = STATE.activeFilter === 'all' || cat === STATE.activeFilter;
      card.classList.toggle('bmb-hidden', !(matchesSearch && matchesFilter));
    });
  }

  /* --------------------------------------------------------
     12. SESSION / LOCAL STATE PERSISTENCE
  -------------------------------------------------------- */
  function saveStateToSession() {
    try {
      sessionStorage.setItem('bmb_state', JSON.stringify({
        items: STATE.items,
        extrasItems: STATE.extrasItems,
        extrasDismissed: STATE.extrasDismissed,
        selectedTier: STATE.selectedTier,
        sellingPlanId: STATE.sellingPlanId,
        panVariantId: STATE.panVariantId,
        isPanCastIron: STATE.isPanCastIron,
        panQty: STATE.panQty || 1,
        panUserIntent: STATE.panUserIntent,
        giftNote: BMB_GIFTING ? (STATE.giftNote || '') : '',
        gifting: BMB_GIFTING
      }));
    } catch (e) {}
  }
  function restoreExtrasFromSaved(saved) {
    STATE.extrasItems = Array.isArray(saved.extrasItems) ? saved.extrasItems : [];
    STATE.extrasDismissed = !!saved.extrasDismissed;
  }
  function restorePanFromSaved(saved) {
    // defaulting to null/false lets syncPanAutoInclude() (called right after
    // restore, in init()) recompute the pan from scratch when this session/
    // cart predates the pan feature, same pattern as bab-custom.js.
    STATE.panVariantId = ('panVariantId' in saved) ? saved.panVariantId : null;
    STATE.isPanCastIron = ('isPanCastIron' in saved) ? saved.isPanCastIron : false;
    STATE.panUserIntent = ('panUserIntent' in saved) ? saved.panUserIntent : null;
    STATE.panQty = 1;
    var qty = ('panQty' in saved) ? parseInt(saved.panQty, 10) : 1;
    if (!STATE.isPanCastIron && qty > 1 && PAN) {
      var leftover = qty - 1;
      var extra = EXTRAS.find(function (e) { return e.variantId === PAN.variantId; });
      var existing = STATE.extrasItems.find(function (i) { return i.variantId === PAN.variantId; });
      if (existing) existing.qty += leftover;
      else STATE.extrasItems.push({
        variantId: PAN.variantId,
        productId: (extra && extra.productId) || PAN.productId,
        title: (extra && extra.title) || PAN.title,
        image: (extra && extra.image) || PAN.image,
        price: extra ? extra.price : (PAN.comparePrice || 4000),
        qty: leftover
      });
    }
  }
  // Restores selectedTier only if it's still a valid, configured size;
  // otherwise keeps the DEFAULT_TIER_SIZE this session already started
  // with (defensive against a stale save from before a tier was removed).
  function restoreTierFromSaved(saved) {
    if ('selectedTier' in saved && BOX_SIZES.some(function (b) { return b.size === saved.selectedTier; })) {
      STATE.selectedTier = saved.selectedTier;
    }
  }
  // Subscription-only (round 16): a leftover saved null (old one-time)
  // must never restore. Coerce back to the 30-day default.
  function restoreGiftNoteFromSaved(saved) {
    if (BMB_GIFTING && saved && saved.giftNote) {
      STATE.giftNote = String(saved.giftNote).trim().slice(0, 250);
    } else {
      STATE.giftNote = '';
    }
  }
  var _planRestored = false;
  function restoreSellingPlanFromSaved(saved) {
    if (BMB_GIFTING) {
      STATE.sellingPlanId = null;
      return true;
    }
    if (saved && saved.gifting === true) {
      return false;
    }
    if (saved && 'sellingPlanId' in saved) {
      if (saved.sellingPlanId == null && !oneTimeAllowed()) {
        return false;
      }
      STATE.sellingPlanId = saved.sellingPlanId;
      if (saved.sellingPlanId != null) _lastSubPlanId = saved.sellingPlanId;
      return true;
    }
    return false;
  }
  function restoreStateFromSession() {
    try {
      var raw = sessionStorage.getItem('bmb_state');
      if (!raw) return false;
      sessionStorage.removeItem('bmb_state');
      var saved = JSON.parse(raw);
      restoreGiftNoteFromSaved(saved);
      if (!saved.items || saved.items.length === 0) return !!STATE.giftNote;
      STATE.items = saved.items;
      remapPersistedItemCollectionIds();
      restoreExtrasFromSaved(saved);
      restoreTierFromSaved(saved);
      _planRestored = restoreSellingPlanFromSaved(saved);
      restorePanFromSaved(saved);
      return true;
    } catch (e) {
      sessionStorage.removeItem('bmb_state');
      return false;
    }
  }
  function restoreFromLocalStorage() {
    try {
      // Storage key bumped to _v4 (round 18): extrasItems is a new
      // persisted field. v3 carts are not read, so they cannot restore
      // without extras and look like a missing extras section.
      var raw = localStorage.getItem('atome_bmb_cart_v4');
      if (!raw) return false;
      var saved = JSON.parse(raw);
      restoreGiftNoteFromSaved(saved);
      if (!saved.items || saved.items.length === 0) return !!STATE.giftNote;
      STATE.items = saved.items;
      remapPersistedItemCollectionIds();
      restoreExtrasFromSaved(saved);
      restoreTierFromSaved(saved);
      _planRestored = restoreSellingPlanFromSaved(saved);
      restorePanFromSaved(saved);
      return true;
    } catch (e) { return false; }
  }
  // A restored cart (session back-nav or localStorage) may carry an
  // isGift line from a promo whose dated window has since closed (or not
  // yet opened). Drop any such line; re-add the current URL gift if it's
  // active now and the restore wiped it out.
  function sanitizeRestoredGiftItems() {
    var promos = buildGiftPromoList();
    STATE.items = STATE.items.filter(function (item) {
      if (!item.isGift) return true;
      if (!item.code) return false;
      var promo = promos.find(function (p) { return normalizeGiftCode(p.code) === normalizeGiftCode(item.code); });
      return !!promo && isGiftPromoActiveNow(promo);
    });
    if (BMB_GIFT_ACTIVE && GIFT_VARIANT_ID) {
      // A URL gift is active now: it wins. Drop any other restored gift
      // line (different code) so only the active one can reach checkout.
      STATE.items = STATE.items.filter(function (i) {
        return !i.isGift || normalizeGiftCode(i.code) === ACTIVE_URL_GIFT_CODE;
      });
      var hasActive = STATE.items.some(function (i) { return i.isGift && normalizeGiftCode(i.code) === ACTIVE_URL_GIFT_CODE; });
      if (!hasActive) STATE.items.push(Object.assign({}, GIFT_DATA));
    } else {
      // No URL gift now: keep only the first restored gift whose promo is
      // still active, so at most one gift item ever reaches checkout.
      var kept = false;
      STATE.items = STATE.items.filter(function (i) {
        if (!i.isGift) return true;
        if (kept) return false;
        kept = true;
        return true;
      });
    }
  }

  /* --------------------------------------------------------
     13. CHECKOUT (Aug 18 2026 pivot): the $0 "Build My Box" dynamic-
     bundle / getDynamicBundleItems path above is DELETED. Copied
     wholesale from the live bab-custom.js's handleCheckout/getBundleId
     (its lines ~2082-2197): ONE parent cart line (the selected BYB
     variant, quantity 1, Item N properties for the real contents,
     selling_plan on the PARENT only), Recharge's getBundleId called
     with the real selections for correct renewal fulfillment, pan/gift
     as separate plain lines.

     TRIPWIRE CHECKED (per task): whether Recharge's bundle config for
     BYB6/10/12 requires the selections' quantity SUM to equal the tier
     size is unconfirmed (no visibility into Recharge's own bundle admin
     UI/API from this repo). Established this round: getBundleId is
     documented as best-effort / non-fatal on the LIVE bab-custom.js
     builder today -- if it throws, the order still fulfills correctly
     via the Item N line-item properties (see its own try/catch comment,
     "Non-fatal: initial order still fulfils via line item properties").
     Proceeding on that basis: attempt getBundleId with the REAL per-item
     selections (their actual quantity, never a padded/invented count),
     warn and continue on failure, exactly like the live builder already
     does. No dummy line items, no Recharge config changes.
     ------------------------------------------------------------------ */
  // Checkout hardening (Sep 4 2026, Lucas). Two problems in the old flow:
  // (1) no step had a deadline, so a stalled request left the shopper frozen
  // behind the overlay (Hotjar); (2) Recharge registration was optional: a
  // missing SDK or a getBundleId error let the order through WITHOUT _rb_id.
  // Without _rb_id Recharge never adds the box contents to the Shopify order,
  // Revy has nothing to pick, and renewals have no contents. Recharge is now a
  // hard gate: no _rb_id, no cart add, no checkout. Every failure lands in
  // showCheckoutError(), which unlocks the page, offers Retry, and after a
  // second consecutive failure points to hello@atomebakery.com.
  var CHECKOUT_RC_READY_WAIT_MS = 3000;   // deferred SDK still loading at click time
  var CHECKOUT_RC_TIMEOUT_MS = 8000;      // getBundleId (QA happy path: ~1s total)
  var CHECKOUT_CART_TIMEOUT_MS = 12000;   // /cart/clear.js and /cart/add.js
  var _checkoutFailures = 0;              // consecutive failures this page load
  var _checkoutStartedAt = 0;

  function checkoutError(reason, cause) {
    var e = new Error(reason);
    e.name = 'CheckoutError';
    e.reason = reason;   // one of the CHECKOUT_REASONS below
    e.cause = cause;
    return e;
  }
  // Closed list, used as the GA4 `reason` param. Do not free-type reasons.
  // recharge_sdk_missing | recharge_timeout | recharge_error | recharge_empty_id
  // recharge_bad_collection | cart_clear_timeout | cart_clear_error
  // cart_add_timeout | cart_add_error | unknown

  function withTimeout(promise, ms, label) {
    var timer;
    var deadline = new Promise(function (_, reject) {
      timer = setTimeout(function () { reject(checkoutError(label + '_timeout')); }, ms);
    });
    return Promise.race([promise, deadline]).then(
      function (v) { clearTimeout(timer); return v; },
      function (e) { clearTimeout(timer); throw e; }
    );
  }

  function fetchWithTimeout(url, opts, ms, label) {
    var ctrl = (typeof AbortController === 'function') ? new AbortController() : null;
    var merged = Object.assign({}, opts || {});
    if (ctrl) merged.signal = ctrl.signal;
    var timer = setTimeout(function () { if (ctrl) ctrl.abort(); }, ms);
    return withTimeout(fetch(url, merged), ms, label).then(
      function (res) { clearTimeout(timer); return res; },
      function (e) {
        clearTimeout(timer);
        if (e && e.name === 'AbortError') throw checkoutError(label + '_timeout', e);
        if (e && e.name === 'CheckoutError') throw e;
        throw checkoutError(label + '_error', e);
      }
    );
  }

  function rechargeReady() {
    return !!(RC_TOKEN && _rcInitDone && window.recharge && window.recharge.bundle &&
      typeof window.recharge.bundle.getBundleId === 'function');
  }

  // The SDK script is loaded with `defer` and init is retried 500ms after
  // DOMContentLoaded. A fast shopper can click Checkout before that.
  function waitForRecharge(ms) {
    return new Promise(function (resolve) {
      var started = Date.now();
      (function tick() {
        initRechargeSDK();
        if (rechargeReady()) return resolve(true);
        if (Date.now() - started >= ms) return resolve(false);
        setTimeout(tick, 150);
      })();
    });
  }

  // ---- Tracking. GA4 for counting, Hotjar for finding the recordings. ----
  // send_to pins the event to the GA4 property so it never lands in the
  // Google Ads tag (AW-11083291497) as a conversion.
  var BMB_GA4_ID = 'G-VBV2TKEGMD';
  function bmbTrack(name, params) {
    var p = Object.assign({ send_to: BMB_GA4_ID, builder: 'bmb' }, params || {});
    try {
      if (typeof window.gtag === 'function') window.gtag('event', name, p);
      else if (Array.isArray(window.dataLayer)) window.dataLayer.push(Object.assign({ event: name }, p));
    } catch (e) { /* analytics must never break checkout */ }
    try {
      if (typeof window.hj === 'function') window.hj('event', name);
    } catch (e) { /* same */ }
    if (window.console && console.debug) console.debug('[BMB track]', name, p);
  }
  function checkoutTrackingBase() {
    var tier = currentTier();
    return {
      tier: tier ? String(tier.size || tier.variantId) : 'none',
      items_count: STATE.items.filter(function (i) { return !i.isGift; }).length,
      has_pan: !!STATE.panVariantId,
      extras_count: STATE.extrasItems.length,
      plan_id: isOneTimeSelected() ? 'onetime' : String(STATE.sellingPlanId || (DEFAULT_SELLING_PLAN && DEFAULT_SELLING_PLAN.id) || ''),
      lang: (window.Weglot && typeof Weglot.getCurrentLang === 'function') ? Weglot.getCurrentLang() : 'en'
    };
  }

  var GIFT_NOTE_HANDLE = 'gift-note';
  function resolveGiftNoteProduct() {
    return fetch('/products/' + GIFT_NOTE_HANDLE + '.js', { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (product) {
        if (!product || !product.variants || !product.variants.length) return null;
        return { productId: product.id, variantId: product.variants[0].id };
      })
      .catch(function () { return null; });
  }

  var SIG_KEY = 'bmb_cart_sig';
  var ATC_KEY = 'bmb_atc_fired';

  function cartStateFingerprint() {
    var items = STATE.items.filter(function (i) { return !i.isGift; })
      .map(function (i) { return String(i.variantId) + ':' + String(i.qty); })
      .sort()
      .join(',');
    var extras = STATE.extrasItems
      .map(function (i) { return String(i.variantId) + ':' + String(i.qty); })
      .sort()
      .join(',');
    var gift = STATE.items.find(function (i) { return i.isGift; });
    return [
      't' + String(STATE.selectedTier || ''),
      'p' + String(STATE.sellingPlanId || (BMB_GIFTING ? 'gift' : '')),
      'pan' + String(STATE.panVariantId || '0'),
      'ci' + (STATE.isPanCastIron ? '1' : '0'),
      'i' + items,
      'x' + extras,
      'g' + String((gift && gift.variantId) || ''),
      'n' + (BMB_GIFTING ? String(STATE.giftNote || '').trim() : '')
    ].join('|');
  }

  function readCommittedSig() {
    try {
      var raw = sessionStorage.getItem(SIG_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }

  function writeCommittedSig(fp, rbId) {
    var payload = { fp: fp, rbId: rbId, at: Date.now() };
    try { sessionStorage.setItem(SIG_KEY, JSON.stringify(payload)); } catch (e) {}
  }

  async function cartHasCommittedBox() {
    try {
      var res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!res.ok) return false;
      var cart = await res.json();
      return (cart.items || []).some(function (line) {
        return line.properties && line.properties._rb_id;
      });
    } catch (e) { return false; }
  }

  async function isCartSignatureFresh() {
    var committed = readCommittedSig();
    if (!committed || !committed.fp) return false;
    if (committed.fp !== cartStateFingerprint()) return false;
    return cartHasCommittedBox();
  }

  // Shopify Facebook & Instagram + Google channels fire standard AddToCart /
  // add_to_cart on /cart/add.js. Do not fire a second manual copy. This flag
  // only records that the first successful commit happened for this box root.
  function markAtcFired(fp) {
    try { sessionStorage.setItem(ATC_KEY, fp); } catch (e) {}
  }

  var _commitLock = null;

  async function commitCartFromState(opts) {
    opts = opts || {};
    if (_commitLock) {
      try { await _commitLock; } catch (e) {}
    }
    _commitLock = doCommitCartFromState(opts);
    try {
      return await _commitLock;
    } finally {
      _commitLock = null;
    }
  }

  async function doCommitCartFromState(opts) {
    var reason = (opts && opts.reason) || 'checkout';
    var realItems = STATE.items.filter(function (i) { return !i.isGift; });
    if (realItems.length === 0) return { ok: false, reason: 'empty' };
    var tier = currentTier();
    if (!tier) return { ok: false, reason: 'no_tier' };

    var fp = cartStateFingerprint();
    var sigGiftItem = STATE.items.find(function (i) { return i.isGift; });
    var sigGiftCode = (sigGiftItem && sigGiftItem.code) ? normalizeGiftCode(sigGiftItem.code) : ACTIVE_URL_GIFT_CODE;
    try {
      if (await isCartSignatureFresh()) {
        return { ok: true, skipped: true, fp: fp, rbId: (readCommittedSig() || {}).rbId, giftCode: sigGiftCode };
      }
    } catch (e) { /* treat as stale */ }

    var rcMs = 0;
    try {
      var clearRes = await fetchWithTimeout('/cart/clear.js', { method: 'POST' }, CHECKOUT_CART_TIMEOUT_MS, 'cart_clear');
      if (!clearRes.ok) throw checkoutError('cart_clear_error');

      var ready = await waitForRecharge(CHECKOUT_RC_READY_WAIT_MS);
      if (!ready) throw checkoutError('recharge_sdk_missing');
      var bundle = {
        externalVariantId: String(tier.variantId),
        externalProductId: String(BYB_PRODUCT_ID),
        selections: realItems.map(function (item) {
          var collectionId = resolveLiveCollectionId(item.variantId, item.collectionId);
          if (!collectionId) {
            console.warn('[BMB] collectionId not allowlisted', item.variantId, item.collectionId);
            throw checkoutError('recharge_bad_collection');
          }
          return {
            collectionId: collectionId,
            externalProductId: String(item.productId),
            externalVariantId: String(item.variantId),
            quantity: item.qty
          };
        })
      };
      var rbId;
      var rcStarted = Date.now();
      try {
        rbId = await withTimeout(recharge.bundle.getBundleId(bundle), CHECKOUT_RC_TIMEOUT_MS, 'recharge');
      } catch (e) {
        if (e && e.name === 'CheckoutError') throw e;
        throw checkoutError('recharge_error', e);
      }
      if (typeof rbId !== 'string' || !rbId.trim()) throw checkoutError('recharge_empty_id');
      rcMs = Date.now() - rcStarted;

      var items = [];
      var boxProperties = {};
      realItems.forEach(function (item, idx) {
        boxProperties['Item ' + (idx + 1)] = (item.titleEn || item.title) + ' \u00d7 ' + item.qty;
      });
      boxProperties._rb_id = rbId;
      if (BMB_GIFTING) boxProperties._gift = 'true';

      var giftMessage = (BMB_GIFTING && String(STATE.giftNote || '').trim()) || '';
      var giftNoteResolved = null;
      if (giftMessage) {
        giftNoteResolved = await resolveGiftNoteProduct();
        if (!giftNoteResolved) boxProperties['Gift message'] = giftMessage;
      }

      var boxItem = { id: tier.variantId, quantity: 1, properties: boxProperties };
      if (!BMB_GIFTING) {
        var planId = effectivePlanId();
        if (planId) boxItem.selling_plan = planId;
      }
      items.push(boxItem);

      if (STATE.panVariantId) {
        items.push({ id: STATE.panVariantId, quantity: 1 });
      }

      var giftItem = STATE.items.find(function (i) { return i.isGift; });
      var checkoutGiftCode = null;
      if (giftItem && giftItem.variantId) {
        checkoutGiftCode = giftItem.code ? normalizeGiftCode(giftItem.code) : ACTIVE_URL_GIFT_CODE;
        var giftLine = { id: giftItem.variantId, quantity: 1 };
        if (checkoutGiftCode) {
          giftLine.properties = { _url_gift: checkoutGiftCode };
        }
        items.push(giftLine);
      }

      STATE.extrasItems.forEach(function (extra) {
        if (!extra.variantId || !extra.qty) return;
        items.push({ id: extra.variantId, quantity: extra.qty });
        if (isBogoExtra(extra)) {
          items.push({
            id: extra.variantId,
            quantity: extra.qty,
            properties: { _bogo_gift: 'Buy 1 get 1 add-on deal' }
          });
        }
      });

      if (giftNoteResolved && giftMessage) {
        items.push({
          id: giftNoteResolved.variantId,
          quantity: 1,
          properties: { 'Gift message': giftMessage, _gift: 'true' }
        });
      }

      var res = await fetchWithTimeout('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      }, CHECKOUT_CART_TIMEOUT_MS, 'cart_add');
      if (!res.ok) {
        var errBody = await res.json().catch(function () { return {}; });
        var addErr = checkoutError('cart_add_error');
        addErr.detail = errBody.description || '';
        throw addErr;
      }

      writeCommittedSig(fp, rbId);
      markAtcFired(fp);
      bmbTrack('bmb_cart_commit', Object.assign({
        reason: reason,
        skipped: false,
        recharge_ms: rcMs
      }, checkoutTrackingBase()));
      return { ok: true, skipped: false, fp: fp, rbId: rbId, recharge_ms: rcMs, giftCode: checkoutGiftCode };
    } catch (err) {
      if (reason === 'extras') {
        console.warn('[BMB] extras cart commit deferred:', (err && err.reason) || err);
        bmbTrack('bmb_cart_commit_defer', Object.assign({
          reason: (err && err.reason) || 'unknown'
        }, checkoutTrackingBase()));
        return { ok: false, reason: (err && err.reason) || 'unknown', deferred: true };
      }
      throw err;
    }
  }

  var _checkoutInProgress = false;
  async function handleCheckout() {
    if (_checkoutInProgress) return;
    var realItems = STATE.items.filter(function (i) { return !i.isGift; });
    if (realItems.length === 0) { alert(S.alertEmptyBox); return; }
    if (!boxIsFull()) return;

    var tier = currentTier();
    if (!tier) { alert(S.alertBoxNotFound); return; }

    _checkoutInProgress = true;
    _checkoutStartedAt = Date.now();
    showLoading(true);
    saveStateToSession();
    bmbTrack('bmb_checkout_start', Object.assign({ attempt: _checkoutFailures + 1 }, checkoutTrackingBase()));

    try {
      var result = await commitCartFromState({ reason: 'checkout' });
      if (!result || !result.ok) throw checkoutError((result && result.reason) || 'unknown');
      _checkoutFailures = 0;
      bmbTrack('bmb_checkout_success', Object.assign({
        elapsed_ms: Date.now() - _checkoutStartedAt,
        recharge_ms: result.recharge_ms || 0,
        skipped: !!result.skipped
      }, checkoutTrackingBase()));
      // Truth and Reconciliation Day gift line (assets/trc-gift.js). Bounded, never throws.
      if (window.AtomeTRC) await window.AtomeTRC.ensure();
      if (result.giftCode) {
        window.location.href = '/discount/' + encodeURIComponent(result.giftCode) + '?redirect=/checkout';
      } else {
        window.location.href = '/checkout';
      }
    } catch (err) {
      _checkoutInProgress = false;
      _checkoutFailures += 1;
      var failReason = (err && err.reason) || 'unknown';
      console.warn('[BMB] checkout stopped:', failReason, err && err.cause || err);
      bmbTrack('bmb_checkout_fail', Object.assign({
        reason: failReason,
        attempt: _checkoutFailures,
        elapsed_ms: Date.now() - _checkoutStartedAt,
        detail: (err && err.detail) ? String(err.detail).slice(0, 100) : ''
      }, checkoutTrackingBase()));
      showCheckoutError(failReason);
    }
  }

  function showLoading(show) {
    if (!el.loadingOverlay) return;
    if (show) el.loadingOverlay.classList.remove('bmb-loading-overlay--error');
    el.loadingOverlay.classList.toggle('bmb-loading-overlay--visible', show);
  }

  // Failure UI inside the existing overlay. Spinner mode is the Liquid
  // markup untouched; error mode appends a panel once and toggles a class.
  function checkoutRef(reason) {
    // Short human-readable reference for the support email and Hotjar cross-check.
    return reason.replace(/_/g, '-') + '-' + Date.now().toString(36).slice(-5);
  }
  function checkoutBoxSummary() {
    var tier = currentTier();
    var parts = STATE.items.filter(function (i) { return !i.isGift; }).map(function (i) {
      return (i.titleEn || i.title) + ' x' + i.qty;
    });
    return (tier ? ('Box ' + (tier.size || tier.variantId) + ': ') : '') + parts.join(', ');
  }
  function showCheckoutError(reason) {
    if (!el.loadingOverlay) return;
    var panel = el.loadingOverlay.querySelector('.bmb-co-error');
    if (!panel) {
      panel = document.createElement('div');
      panel.className = 'bmb-co-error';
      panel.setAttribute('role', 'alertdialog');
      panel.setAttribute('aria-live', 'assertive');
      panel.innerHTML =
        '<h3 class="bmb-co-error__title"></h3>' +
        '<p class="bmb-co-error__body"></p>' +
        '<button type="button" class="bmb-cta-btn bmb-co-error__retry"></button>' +
        '<button type="button" class="bmb-co-error__back"></button>' +
        '<p class="bmb-co-error__ref"></p>';
      el.loadingOverlay.appendChild(panel);
      panel.querySelector('.bmb-co-error__retry').addEventListener('click', function () {
        bmbTrack('bmb_checkout_retry', { attempt: _checkoutFailures + 1 });
        handleCheckout();
      });
      panel.querySelector('.bmb-co-error__back').addEventListener('click', function () {
        showLoading(false);
      });
      panel.querySelector('.bmb-co-error__body').addEventListener('click', function (e) {
        if (e.target && e.target.matches('a[href^="mailto:"]')) bmbTrack('bmb_checkout_help_click', { attempt: _checkoutFailures });
      });
    }
    var ref = checkoutRef(reason);
    var again = _checkoutFailures >= 2;
    panel.querySelector('.bmb-co-error__title').textContent = S.coTitle;
    var body = panel.querySelector('.bmb-co-error__body');
    if (again) {
      var mailto = 'mailto:hello@atomebakery.com' +
        '?subject=' + encodeURIComponent(S.coEmailSubject) +
        '&body=' + encodeURIComponent(S.coEmailBody.replace('{ref}', ref).replace('{box}', checkoutBoxSummary()));
      body.innerHTML = S.coBodyAgain.replace('hello@atomebakery.com',
        '<a href="' + mailto + '">hello@atomebakery.com</a>');
    } else if (reason === 'recharge_bad_collection') {
      body.textContent = S.coBodyCollection || S.coBodyFirst;
    } else {
      body.textContent = S.coBodyFirst;
    }
    panel.querySelector('.bmb-co-error__retry').textContent = S.coRetry;
    panel.querySelector('.bmb-co-error__back').textContent = S.coBack;
    panel.querySelector('.bmb-co-error__ref').textContent = ref;
    el.loadingOverlay.classList.add('bmb-loading-overlay--visible', 'bmb-loading-overlay--error');
    panel.querySelector('.bmb-co-error__retry').focus();
  }

  /* --------------------------------------------------------
     13a. SHIPPING ESTIMATE POPUP (round 14, Aug 18 2026): reuses the
     EXISTING f2c-delivery storefront app-proxy API already live on
     /pages/delivery-area (same discovery-probe + POST pattern as
     F2C Delivery's own extensions/f2c-delivery-widget/assets/
     delivery-checker.js, read-only reference, never edited). Never
     builds a new endpoint, never touches f2c-delivery code or the
     Atome warehouse config, and never reads/writes the app's own
     freeShippingMaxCoverage: the $35 coverage cap applied below is a
     DISPLAY-ONLY figure local to this popup (SHIPCALC_MAX_COVERAGE_CENTS),
     driven by the SAME client-side qualification rule the free-shipping
     PILL already uses (tierQualifiesFreeShipping(): selected tier's
     pay price for the active plan >= threshold), since the API's own POST body
     here is postal-only and carries no cart total.
  -------------------------------------------------------- */
  var SHIPCALC_STORAGE_KEY = 'f2c-proxy-path'; // same key the live widget uses, so a warm cache is shared across pages
  var SHIPCALC_POSTAL_RE = /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/;
  var shipcalcResolvedPath = null;

  function shipcalcProxyCandidates() {
    var base = '/apps/f2c-delivery';
    var list = [];
    if (SHIPCALC_PROXY_OVERRIDE) list.push(SHIPCALC_PROXY_OVERRIDE.replace(/\/$/, ''));
    if (list.indexOf(base) === -1) list.push(base);
    for (var i = 1; i <= 3; i++) {
      var p = base + '-' + i;
      if (list.indexOf(p) === -1) list.push(p);
    }
    return list;
  }
  function shipcalcReadCachedPath() {
    try { return window.localStorage.getItem(SHIPCALC_STORAGE_KEY); } catch (e) { return null; }
  }
  function shipcalcWriteCachedPath(path) {
    try { window.localStorage.setItem(SHIPCALC_STORAGE_KEY, path); } catch (e) {}
  }
  function shipcalcProbePath(path) {
    return fetch(path + '/rates', { method: 'GET', headers: { Accept: 'application/json' } })
      .then(function (resp) {
        if (!resp.ok) throw new Error('probe failed');
        return resp.json();
      })
      .then(function (data) {
        if (data && data.f2c === true) return path;
        throw new Error('not an F2C endpoint');
      });
  }
  function shipcalcDiscoverProxyPath() {
    var candidates = shipcalcProxyCandidates();
    var cached = shipcalcReadCachedPath();
    if (cached && candidates.indexOf(cached) > 0) {
      candidates.splice(candidates.indexOf(cached), 1);
      candidates.unshift(cached);
    }
    var idx = 0;
    function tryNext() {
      if (idx >= candidates.length) return Promise.reject(new Error('no F2C proxy path found'));
      var candidate = candidates[idx++];
      return shipcalcProbePath(candidate).catch(tryNext);
    }
    return tryNext().then(function (path) {
      shipcalcResolvedPath = path;
      shipcalcWriteCachedPath(path);
      return path;
    });
  }
  function shipcalcGetProxyPath() {
    if (shipcalcResolvedPath) return Promise.resolve(shipcalcResolvedPath);
    return shipcalcDiscoverProxyPath();
  }
  // Warm up discovery in the background so the first click in the popup is instant.
  shipcalcGetProxyPath().catch(function () {});

  function shipcalcFmt(n) { return '$' + (Math.round(n * 100) / 100).toFixed(2); }
  function shipcalcEscapeHtml(str) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str == null ? '' : String(str)));
    return div.innerHTML;
  }
  function shipcalcFormatShipDate(isoStr) {
    var parts = String(isoStr).split('-');
    var d = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
    if (isNaN(d.getTime())) return isoStr;
    return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  }

  var shipcalcEl = { overlay: null, input: null, btn: null, error: null, loading: null, result: null, close: null };

  function shipcalcShowError(msg) {
    if (!shipcalcEl.error) return;
    shipcalcEl.error.textContent = msg;
    shipcalcEl.error.style.display = 'block';
    if (shipcalcEl.result) shipcalcEl.result.style.display = 'none';
    if (shipcalcEl.loading) shipcalcEl.loading.style.display = 'none';
    if (shipcalcEl.btn) shipcalcEl.btn.disabled = false;
  }
  function shipcalcHideError() {
    if (shipcalcEl.error) shipcalcEl.error.style.display = 'none';
  }
  function shipcalcShowLoading() {
    if (shipcalcEl.loading) shipcalcEl.loading.style.display = 'flex';
    if (shipcalcEl.result) shipcalcEl.result.style.display = 'none';
    shipcalcHideError();
    if (shipcalcEl.btn) shipcalcEl.btn.disabled = true;
  }
  function shipcalcHideLoading() {
    if (shipcalcEl.loading) shipcalcEl.loading.style.display = 'none';
    if (shipcalcEl.btn) shipcalcEl.btn.disabled = false;
  }

  // Light result: ETA, "starting around" the cheapest rate (not a
  // min-to-max range), dry-ice note, one quiet disclaimer. Green chip
  // when the selected tier already qualifies; upgrade CTA on the 6 only.
  function shipcalcRenderResult(data) {
    shipcalcHideLoading();
    if (!shipcalcEl.result) return;
    shipcalcEl.result.style.display = 'block';

    if (!data || data.eligible === false) {
      shipcalcEl.result.innerHTML = '<p class="bmb-shipcalc__ineligible">' + shipcalcEscapeHtml((data && data.message) || S.shipcalcIneligible) + '</p>';
      return;
    }

    var rates = data.rates || [];
    var prices = rates.map(function (r) { return parseFloat(r.price) || 0; });
    var qualifies = tierQualifiesFreeShipping();
    var html = '';

    var deliveryDates = [];
    rates.forEach(function (r) {
      if (r.estimatedDeliveryHuman && deliveryDates.indexOf(r.estimatedDeliveryHuman) === -1) deliveryDates.push(r.estimatedDeliveryHuman);
    });
    if (deliveryDates.length) {
      html += '<p class="bmb-shipcalc__delivery-est">' + S.shipcalcDeliveryEst.replace('{date}', shipcalcEscapeHtml(deliveryDates.join(' / '))) + '</p>';
    }

    if (prices.length) {
      var minPrice = Math.min.apply(null, prices);
      var startStr = (S.shipcalcStartingAround || 'Starting around {price}').replace('{price}', shipcalcFmt(minPrice));
      html += '<p class="bmb-shipcalc__paid-range">' + startStr + '</p>';
    }

    html += '<p class="bmb-shipcalc__dryice">' + S.shipcalcDryIceNote + '</p>';
    html += '<p class="bmb-shipcalc__disclaimer">' + S.shipcalcEstimateDisclaimer + '</p>';

    if (qualifies) {
      html += '<p class="bmb-shipcalc__free-chip"><span aria-hidden="true">✓</span> ' + S.shipcalcFreeChip + '</p>';
    } else if (STATE.selectedTier < 10) {
      html += '<button type="button" class="bmb-shipcalc__upgrade" data-action="shipcalc-upgrade">' + S.shipcalcUpgradeBtn + '</button>';
    }

    shipcalcEl.result.innerHTML = html;
  }

  function shipcalcCheckDelivery() {
    if (!shipcalcEl.input) return;
    var postalCode = (shipcalcEl.input.value || '').trim().toUpperCase();
    if (!postalCode) { shipcalcShowError(S.shipcalcErrEmpty); return; }
    if (!SHIPCALC_POSTAL_RE.test(postalCode)) { shipcalcShowError(S.shipcalcErrInvalid); return; }

    shipcalcShowLoading();

    shipcalcGetProxyPath()
      .then(function (path) {
        return fetch(path + '/rates', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ postalCode: postalCode })
        });
      })
      .then(function (resp) {
        if (resp.status === 429) throw new Error('rate_limited');
        return resp.json();
      })
      .then(function (data) { shipcalcRenderResult(data); })
      .catch(function (err) {
        shipcalcHideLoading();
        console.warn('[BMB] shipping estimate request failed:', err);
        shipcalcShowError(err && err.message === 'rate_limited' ? S.shipcalcErrRateLimited : S.shipcalcErrGeneric);
      });
  }

  function openShipCalc() {
    if (!shipcalcEl.overlay) return;
    shipcalcEl.overlay.classList.add('bmb-modal-overlay--open');
    setScrollLock('shipcalc', true);
    if (shipcalcEl.input) setTimeout(function () { shipcalcEl.input.focus(); }, 50);
  }
  function closeShipCalc() {
    if (!shipcalcEl.overlay) return;
    shipcalcEl.overlay.classList.remove('bmb-modal-overlay--open');
    setScrollLock('shipcalc', false);
  }
  function initShipCalc() {
    shipcalcEl.overlay = $('bmb-shipcalc-overlay');
    shipcalcEl.input   = $('bmb-shipcalc-input');
    shipcalcEl.btn     = $('bmb-shipcalc-btn');
    shipcalcEl.error   = $('bmb-shipcalc-error');
    shipcalcEl.loading = $('bmb-shipcalc-loading');
    shipcalcEl.result  = $('bmb-shipcalc-result');
    shipcalcEl.close   = $('bmb-shipcalc-close');
    if (!shipcalcEl.overlay) return;

    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-action="open-shipcalc"]')) { openShipCalc(); }
      if (e.target.closest('[data-action="shipcalc-upgrade"]')) {
        selectTier(10);
        closeShipCalc();
      }
    });
    if (shipcalcEl.close) shipcalcEl.close.addEventListener('click', closeShipCalc);
    shipcalcEl.overlay.addEventListener('click', function (e) { if (e.target === shipcalcEl.overlay) closeShipCalc(); });
    if (shipcalcEl.btn) shipcalcEl.btn.addEventListener('click', shipcalcCheckDelivery);
    if (shipcalcEl.input) {
      shipcalcEl.input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); shipcalcCheckDelivery(); }
      });
      shipcalcEl.input.addEventListener('input', function () {
        shipcalcHideError();
        var val = shipcalcEl.input.value.replace(/\s/g, '').toUpperCase();
        shipcalcEl.input.value = val.length > 3 ? (val.substring(0, 3) + ' ' + val.substring(3, 6)) : val;
      });
    }
  }

  /* --------------------------------------------------------
     13b. SITE-WIDE CHAT WIDGET (round 10, Aug 16 2026, item 2)
     Identified via a served-HTML + bundle-loader inspection (no blind
     selector guess): the site runs Gorgias Live Chat & Helpdesk as a theme
     app embed block (shopify://apps/gorgias-live-chat-helpdesk/blocks/gorgias/…),
     which injects <script data-gorgias-loader-chat src="https://config.gorgias.chat/…">
     into <body>. That loader in turn appends a second script
     (#gorgias-chat-bundle) which is the thing that actually builds the
     chat bubble/iframes, asynchronously, so the bubble's DOM never appears
     in the page's initial served HTML, confirmed by curling both the
     served page and the loader script directly.
     Rather than guess at that async bundle's container markup, this uses
     Gorgias' own PUBLIC, documented API for exactly this use case
     (docs.gorgias.com "Remove the Chat on specific page(s) of the store" /
     "Display the chat widget after some time…"): call
     `GorgiasChat.init()` (or wait for their `gorgias-widget-loaded` event
     if the global isn't ready yet), then `GorgiasChat.hideChat(true)`.
     This only ever runs because this section (bmb-builder.liquid) loaded
     this script, i.e. only on the BMB builder page, every other page's
     Gorgias install is completely untouched. A CSS backstop targets the
     one container id Gorgias' own docs confirm (#gorgias-chat-container),
     scoped via a body class this same function sets, in case the widget
     briefly flashes before the API promise resolves.
  -------------------------------------------------------- */
  function hideGorgiasChatWidget() {
    try {
      document.body.classList.add('bmb-page');
      var initPromise = window.GorgiasChat
        ? window.GorgiasChat.init()
        : new Promise(function (resolve) {
            window.addEventListener('gorgias-widget-loaded', function () { resolve(); }, { once: true });
          });
      initPromise.then(function () {
        if (window.GorgiasChat && typeof window.GorgiasChat.hideChat === 'function') {
          window.GorgiasChat.hideChat(true);
        } else {
          var el2 = document.getElementById('gorgias-chat-container');
          if (el2) el2.style.display = 'none';
        }
      }).catch(function () {});
    } catch (e) {
      console.warn('[BMB] hideGorgiasChatWidget failed:', e);
    }
  }

  /* --------------------------------------------------------
     14. INIT
  -------------------------------------------------------- */
  function logCollectionSources() {
    var sources = DATA.collectionSources || [];
    if (!sources.length) return;
    sources.forEach(function (s) {
      if (s.source === 'legacy') {
        console.debug('[BMB] Category "' + s.label + '": using LEGACY fallback collection "' + s.resolvedHandle + '" (configured "' + s.configuredHandle + '" is missing or empty)');
      } else {
        console.debug('[BMB] Category "' + s.label + '": using configured collection "' + s.resolvedHandle + '"');
      }
    });
  }

  function init() {
    el.stickyContent  = document.getElementById('bmb-sticky-content');
    el.chooseSize     = $('bmb-choose-size');
    el.sidebarFreq    = $('bmb-sidebar-freq');
    el.drawerFreq     = $('bmb-drawer-freq');
    el.backdrop       = $('bmb-backdrop');
    el.drawer         = $('bmb-drawer');
    el.drawerBody     = $('bmb-drawer-body');
    el.drawerFooter   = $('bmb-drawer-footer');
    el.drawer2        = $('bmb-drawer2');
    el.drawer2Body    = $('bmb-drawer2-body');
    el.drawer2Footer  = $('bmb-drawer2-footer');
    el.productModal   = $('bmb-product-modal');
    el.giftNoteModal  = $('bmb-gift-note-modal');
    el.loadingOverlay = $('bmb-loading-overlay');
    el.panWarning     = $('bmb-pan-warning');
    el.sidebarBody    = $('bmb-sidebar-body');
    el.sidebarFooter  = $('bmb-sidebar-footer');
    el.sidebarHeadCount = $('bmb-sidebar-head-count');
    el.drawerHeadCount  = $('bmb-drawer-head-count');
    el.floatPill = document.getElementById('bab-float-pill');
    el.floatPillText = document.getElementById('bab-float-pill-text');
    if (el.floatPill) {
      el.floatPill.style.display = 'none';
      el.floatPill.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (itemCount() > 0) openDrawer();
      }, true);
    }

    logCollectionSources();
    hideGorgiasChatWidget();
    initShipCalc();

    // Gift, added once on load if the URL param matches.
    if (BMB_GIFT_ACTIVE && GIFT_VARIANT_ID) {
      STATE.items.push(Object.assign({}, GIFT_DATA));
    }

    // Restore: prefer back-nav session state, else last localStorage cart.
    var restored = restoreStateFromSession();
    if (!restored) restored = restoreFromLocalStorage();
    sanitizeRestoredGiftItems();
    if (BMB_GIFTING) {
      STATE.sellingPlanId = null;
    } else if (!_planRestored && STATE.sellingPlanId == null && DEFAULT_SELLING_PLAN) {
      STATE.sellingPlanId = DEFAULT_SELLING_PLAN.id;
    }
    try {
      if (oneTimeAllowed() && new URLSearchParams(window.location.search).get('plan') === 'once') {
        STATE.sellingPlanId = null;
      }
    } catch (e) {}
    var appRoot = document.getElementById('bmb-app');
    if (appRoot && BMB_GIFTING) appRoot.setAttribute('data-gifting', '1');
    if (!BMB_GIFTING) STATE.giftNote = '';
    if (BMB_GIFTING) {
      var sideCol = document.querySelector('.bmb-sidebar-col');
      if (sideCol) sideCol.setAttribute('aria-label', S.yourBoxGift);
    }
    // Recomputes the pan for carts restored without pan fields (pre-dating
    // this feature) and any restored cart whose contents no longer match
    // its saved pan state, same call bab-custom.js makes after restore.
    syncPanAutoInclude();
    applyHubSeed();

    if (el.chooseSize) {
      el.chooseSize.addEventListener('click', function (e) {
        if (e.target.closest('[data-action="open-shipcalc"]')) return;
        var card = e.target.closest('.bmb-size-card');
        if (card && card.dataset.size) selectTier(card.dataset.size);
      });
      el.chooseSize.addEventListener('keydown', function (e) {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        if (e.target.closest('[data-action="open-shipcalc"]')) return;
        var card = e.target.closest('.bmb-size-card');
        if (card && card.dataset.size && e.target === card) {
          e.preventDefault();
          selectTier(card.dataset.size);
        }
      });
    }

    document.addEventListener('click', function (e) {
      var filterBtn = e.target.closest('.bmb-filter-btn');
      if (filterBtn && filterBtn.dataset.filter) applyFilter(filterBtn.dataset.filter);
    });

    var searchInput = document.getElementById('bmb-search');
    if (searchInput) searchInput.addEventListener('input', function () { filterBySearch(searchInput.value); });

    var fillDiscoveryBtn = $('bmb-fill-discovery-btn');
    if (fillDiscoveryBtn) fillDiscoveryBtn.addEventListener('click', fillDiscoveryBox);

    insertCategoryHeaders();
    applyFilter('all');

    // #bmb-bestsellers-row included (round 6) so the golden cards get the
    // exact same add/qty/info-modal handling as regular cards for free.
    document.querySelectorAll('.bmb-grid, #bmb-grid, #bmb-bestsellers-row').forEach(function (grid) {
      grid.addEventListener('click', function (e) {
        var infoHint = e.target.closest('.bmb-product-card__info-hint, .bmb-product-card__img-wrap');
        if (infoHint) {
          var infoCard = infoHint.closest('.bmb-product-card');
          if (infoCard) {
            var meta = infoCard.querySelector('.bmb-product-meta');
            openProductModal({
              title: infoCard.dataset.title,
              image: infoCard.dataset.image,
              description: infoCard.dataset.description,
              ingredients: meta ? (meta.querySelector('[data-field="ingredients"]') || {}).innerHTML : '',
              allergens: meta ? (meta.querySelector('[data-field="allergens"]') || {}).innerHTML : '',
              instructions: meta ? (meta.querySelector('[data-field="instructions"]') || {}).innerHTML : '',
              nutriImg: meta ? (meta.querySelector('[data-field="nutri-img"]') || {}).textContent : ''
            });
          }
          return;
        }

        var btn = e.target.closest('.bmb-atb-btn');
        if (btn) {
          if (btn.classList.contains('bmb-atb-btn--oos') || btn.disabled) return;
          var card = btn.closest('.bmb-product-card');
          if (card && card.dataset.comingSoon === 'true') return;
          if (card && card.dataset.available !== 'false') {
            var nameEl = card.querySelector('.bmb-product-card__name');
            addItem(
              parseInt(card.dataset.variantId, 10),
              parseInt(card.dataset.productId, 10),
              parseInt(card.dataset.collectionId, 10),
              (nameEl && nameEl.textContent.trim()) || card.dataset.title,
              card.dataset.title, // titleEn, always English (data-* attrs are never Weglot-translated)
              card.dataset.image
            );
          }
          return;
        }

        var minusBtn = e.target.closest('.bmb-qty__btn--minus');
        var plusBtn = e.target.closest('.bmb-qty__btn--plus');
        if ((minusBtn || plusBtn) && !(plusBtn && plusBtn.disabled)) {
          var qtyCard = (minusBtn || plusBtn).closest('.bmb-product-card');
          if (qtyCard) changeItemQty(parseInt(qtyCard.dataset.variantId, 10), minusBtn ? -1 : +1);
        }
      });
    });

    // Sticky-bar's own button is rebuilt on every render (renderStickyBar),
    // so it's wired there, not here.

    if (el.drawer) {
      var closeBtn = el.drawer.querySelector('.bmb-drawer__close');
      if (closeBtn) closeBtn.addEventListener('click', closeAllPanels);
    }
    if (el.drawer2) {
      var d2Back = $('bmb-drawer2-back');
      var d2Close = $('bmb-drawer2-close');
      if (d2Back) d2Back.addEventListener('click', closeExtrasPanel);
      if (d2Close) d2Close.addEventListener('click', closeAllPanels);
    }
    if (el.backdrop) el.backdrop.addEventListener('click', function () { closeAllPanels(); closeProductModal(); });

    // Escape closes the topmost open overlay (scroll-lock audit, Aug 26
    // 2026: previously NO overlay closed on Escape at all). Order:
    // pan warning, product modal, shipcalc, extras drawer, review
    // drawer. Each close path releases its own scroll-lock key via
    // setScrollLock, so Escape can never strand a hidden body overflow.
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' && e.key !== 'Esc') return;
      if (el.giftNoteModal && el.giftNoteModal.classList.contains('bmb-modal-overlay--open')) { closeGiftNoteModal(); return; }
      if (el.panWarning && el.panWarning.classList.contains('bmb-modal-overlay--open')) { closePanWarning(); return; }
      if (el.productModal && el.productModal.classList.contains('bmb-modal-overlay--open')) { closeProductModal(); return; }
      if (shipcalcEl.overlay && shipcalcEl.overlay.classList.contains('bmb-modal-overlay--open')) { closeShipCalc(); return; }
      if (STATE.activePanel === 'extras') { closeExtrasPanel(); return; }
      if (STATE.activePanel === 'drawer') { closeAllPanels(); }
    });

    // BOGO add-on timer (Sep 2026 r3): 1s tick on [data-bmb-bogo-countdown]
    // ("2 days 04:12:33", then "04:12:33" under a day). When bogo_end
    // passes: hide the page + extras modules and one render() so gift
    // rows stop (bogoActive() is already false by then).
    function bogoTick() {
      if (!bogoActive()) return false;
      var text = bogoCountdownText();
      document.querySelectorAll('[data-bmb-bogo-countdown]').forEach(function (n) { n.textContent = text; });
      return true;
    }
    if (BOGO_ENDS_AT && bogoActive()) {
      bogoTick();
      var bogoTicker = setInterval(function () {
        if (!bogoTick()) { clearInterval(bogoTicker); render(); }
      }, 1000);
    }

    // Event countdown ticker: refresh the "Order within ..." line every
    // 30s without a full re-render; when the deadline passes, one full
    // render() drops the whole event block everywhere.
    if (EVENT_LABEL && EVENT_DEADLINE && !eventExpired()) {
      var eventTicker = setInterval(function () {
        if (eventExpired()) {
          clearInterval(eventTicker);
          render();
          return;
        }
        var text = eventCountdownText();
        document.querySelectorAll('[data-bmb-countdown]').forEach(function (n) { n.textContent = text; });
      }, 30000);
    }

    if (el.productModal) {
      var modalClose = el.productModal.querySelector('.bmb-modal__close');
      if (modalClose) modalClose.addEventListener('click', closeProductModal);
      el.productModal.addEventListener('click', function (e) { if (e.target === el.productModal) closeProductModal(); });
    }

    if (el.panWarning) {
      var panConfirmBtn = el.panWarning.querySelector('#bmb-pan-warning-confirm');
      var panCancelBtn = el.panWarning.querySelector('#bmb-pan-warning-cancel');
      if (panConfirmBtn) panConfirmBtn.addEventListener('click', function () { closePanWarning(); removePan(); });
      if (panCancelBtn) panCancelBtn.addEventListener('click', closePanWarning);
    }

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter') return;
      var tag = (e.target && e.target.tagName) ? e.target.tagName.toLowerCase() : '';
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      var yes = document.querySelector('.bmb-pan-prompt [data-action="pan-prompt-yes"]');
      if (!yes) return;
      e.preventDefault();
      STATE.panJustAdded = true;
      setPan(false);
    });

    document.addEventListener('click', function (e) {
      var giftBtn = e.target.closest && e.target.closest('[data-action]');
      if (!giftBtn) return;
      var giftAction = giftBtn.getAttribute('data-action');
      if (giftAction === 'open-gift-note' || giftAction === 'edit-gift-note') {
        e.preventDefault();
        openGiftNoteModal();
      } else if (giftAction === 'remove-gift-note') {
        e.preventDefault();
        STATE.giftNote = '';
        render();
      } else if (giftAction === 'save-gift-note') {
        e.preventDefault();
        saveGiftNoteFromModal();
      } else if (giftAction === 'cancel-gift-note' || giftAction === 'close-gift-note') {
        e.preventDefault();
        closeGiftNoteModal();
      }
    });
    if (el.giftNoteModal) {
      el.giftNoteModal.addEventListener('click', function (e) {
        if (e.target === el.giftNoteModal) closeGiftNoteModal();
      });
    }

    var sidebarContinue = document.getElementById('bmb-sidebar-body');
    if (sidebarContinue) { /* review CTA lives in the drawer/footer; desktop sidebar footer's checkout button is wired in renderDesktopSidebar() */ }

    var appEl = document.getElementById('bmb-app');
    if (appEl) appEl.classList.add('bmb-js-ready');

    render();

    // Round 9 fix (Aug 16 2026), Lucas's direction: the drawer must NEVER
    // auto-open on load, on any device. Desktop already has the
    // always-visible inline sidebar; on mobile the drawer opens only from
    // an explicit user tap (floating pill, sticky-bar review button, or
    // the mobile-only add-triggered open in fillDiscoveryBox() above).
    // Previously this line reopened the drawer on every back-nav/restore
    // even though nothing was clicked.
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose a small hook for the "review your box" affordance from the sticky bar text.
  window.addEventListener('bmb:open-drawer', openDrawer);

  // Back from /checkout restores this page from bfcache exactly as it was
  // left: overlay up, checkout locked. Reset both. Minimal on purpose: no
  // drawer, modal, or state changes (Round 9 rule: drawer never auto-opens).
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    _checkoutInProgress = false;
    showLoading(false);
  });

})();
